import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence } from "framer-motion";
import maplibregl from "./vendor-maplibre.js";
import { html, M, F, Ico, _Sec, KV, Alert, _Ticker, SPRING, SOFT, FADE, go, stop, hashSearchParams } from "./ui.js";
import * as D from "./data.js";
import * as E from "./edge-data.js";
import { useLive, UNKNOWN } from "./live.js";
import { initialsOf, useSession } from "./session.js";
import { api, path, useApi, _useApi, _useAction, usePolled } from "./api.js";
import { holdTeamRunOrRefuse, continueTeamRunPrefer, continueRefuseToast, holdRefuseToast } from "./hold-team-runtime.js";
import { useEventsLive, useLocationsLive } from "./stream-live.js";
import { openLiveSession, getLiveSession, renewLiveSession, closeLiveSession } from "./udock-api.js";
import { RemuxPullDesk } from "./udock-live-desk.js";
import { GrantLatticePlayer } from "./udock-grant-lattice-player.js";
import { _presenceDiagnoseView } from "./fleet-live.js";
import { _rssiOf, _vbatOf } from "./device-overview-live.js";
import { DecisionPulse } from "./ai-context-rail.js";
import { OpsAiWorkspace } from "./ops-ai-workspace.js";
import { isLocalHost } from "./pathseal.js";
import { boardHeadingOf, operatorSay } from "./operator-say.js";
import { useSoftFleetSim } from "./soft-fleet-sim.js";
import {
  syncSoftFleetMarkers,
  raiseSoftRouteLayers,
  softTryPriorityFrame,
  tickSoftAiDirector,
  frameSoftFocus,
  frameSoftVolume,
  SOFT_DASH_WALK,
  SOFT_DASH_AIR,
  SOFT_DASH_BIKE,
} from "./soft-fleet-glyphs.js";
import { OpsAlarmsDesk } from "./ops-alarms-desk.js";
import { OpsOverviewDesk } from "./ops-overview-desk.js";
import { OpsDispatchDesk } from "./ops-dispatch-desk.js";
import { OpsCommandWorkbench } from "./ops-command-workbench.js";
import { OpsMapPlaneLegend, OpsVolumeMapPlate, OpsVolumeMapRim } from "./ops-command-surface.js";
import { OpsDevicesDesk } from "./ops-devices-desk.js";
import { OpsDeviceInspectDesk } from "./ops-device-inspect-desk.js";
import { OpsMapLayersDesk, OpsMapControlsDesk, deviceSelIdOf, mapEventPinOf, assignExternalPinLive, useOpsMapSeat } from "./ops-map-chrome.js";
import { OpsMapFocusDesk } from "./ops-map-focus-desk.js";
import {
  applyMapFocus,
  emptyFocus,
  enterMapFocus,
  focusCamera,
  focusLabel,
  focusPlaceKey,
  leaveMapFocus,
  loadMapFocus,
  pointInFocus,
  ringInFocus,
  saveMapFocus,
} from "./ops-map-focus.js";
import { buildGroupLinkFeatures } from "./ops-group-mesh.js";
import { OpsPersonSheetDesk, usePersonnelTrack } from "./ops-person-sheet-desk.js";
import { _DeviceCamerasLive, _enqueueDeviceSnapshot } from "./device-cameras.js";
import { installOpsRoadGraph, opsMapPixelRatio } from "./ops-map-roads.js";
import {
  installOpsMapFluency,
  opsMapCamYield,
  opsMapDeferSourceData,
  opsMapFluencyConstructOpts,
} from "./ops-map-fluency.js";
import { syncOpsMapViewport, opsMapContainerOk } from "./ops-map-viewport.js";
import {
  OPS_MAP_MIN_ZOOM,
  OPS_MAP_MAX_ZOOM_SATELLITE,
  OPS_MAP_DEFAULT_ZOOM_REGION,
  clampOpsMapZoom,
} from "../lib/ops-map-limits.js";
import { OpsHwMark, opsHwPinMetaOf } from "./ops-hw-mark.js";
import {
  softZoneCatalogOf,
  softMapLiveThumbsOf,
  loadActiveZoneId,
  saveActiveZoneId,
  softZoneOriginOf,
  softZoneVolumeVmOf,
  softVolumeMapContextOf,
  softLngLatInRadius,
  softRingBoundsOf,
} from "./ops-zone-fabric.js";
import {
  enrichFencePulseProps,
  fenceFillColorExpr,
  fenceLineColorExpr,
  tickZonePulseStates,
  buildZoneBeacons,
} from "./ops-zone-pulse.js";
import {
  buildAiSimSignals,
  buildAiCortexPack,
  buildAiFlowSparks,
  aiLoopSummary,
} from "./ops-ai-zone-sim.js";
import {
  syncOpsMapLiveThumbs,
  enrichMapThumbsWithRegistry,
  liveRegistryMapThumbsOf,
  mergeMapThumbHosts,
  livePreviewSourceIdsOf,
  personnelLiveLampOf,
  applyAiLiveMapSeats,
} from "./ops-map-live-thumbs.js";
import {
  syncOpsLivePinPool,
  clearOpsLivePinPool,
  wireOpsLivePinCamera,
} from "./ops-map-device-pins.js";
import {
  loadRegionIds,
  saveRegionIds,
  toggleRegionId,
  regionMatcherOf,
  regionLabelOf,
  loadSiteIds,
  saveSiteIds,
  deviceMatchesSites,
  fenceMatchesSites,
  siteLabelOf,
  loadAutoLivePref,
  saveAutoLivePref,
  autoLiveEnabled,
  nextAutoLiveAlarm,
} from "./ops-map-region-filter.js";
import {
  fetchAndSyncAiMapOps,
  clearAiMapOpsLayers,
  setAiMapOpsVisibility,
  loopHudFromMapOps,
  closeWaitCopy,
} from "./ops-map-ai-layers.js";
import { OpsZonesDesk } from "./ops-zone-ops-desk.js";
import { OpsResourceOntologyDesk } from "./ops-resource-ontology-desk.js";
import {
  _OpsPanelRail,
  OpsPanelReveal,
  OpsObserveChrome,
  opsPanelTabLabelsOf,
  opsPanelFillTab,
  opsPanelItemOf,
  opsPanelFaceOf,
  OPS_PANEL_NAV,
} from "./ops-panel-rail.js";

const { CENTRE } = D;

/**
 * Prefer the live answer where we have one. The device register itself is
 * live too — an empty list means this account has no devices, not that the
 * console should invent fourteen demo ones.
 */
function useSite() {
  const live = useLive();
  if (!live) {
    return {
      devices: [],
      offline: [],
      people: [],
      alarms: [],
      events: [],
      fences: {},
      zones: [],
      centre: CENTRE,
      devicesReady: false,
      peopleReady: false,
      zonesReady: false,
      belief: null,
      gate: null,
      soft: { intents: UNKNOWN, plans: UNKNOWN, bound: 0 },
      ops: { notify: UNKNOWN, siren: UNKNOWN, sirenTone: "" },
    };
  }
  return {
    people: live.people || [],
    peopleSources: live.peopleSources,
    peopleReady: live.peopleReady,
    fences: live.fences || {},
    zones: live.zones || [],
    zonesReady: live.zonesReady,
    alarms: live.alarms || [],
    events: live.events || [],
    belief: live.belief,
    gate: live.gate,
    soft: live.soft || { intents: UNKNOWN, plans: UNKNOWN, bound: 0 },
    ops: live.ops || { notify: UNKNOWN, siren: UNKNOWN, sirenTone: "" },
    devices: live.devices || [],
    offline: live.offline || [],
    centre: live.centre || CENTRE,
    devicesReady: live.devicesReady,
  };
}

/** Overlay consent-scoped phone SSE fixes onto the personnel roster. */
export function opsPeopleWithLocationsOf(people, payload) {
  const roster = Array.isArray(people) ? people : [];
  const rows = payload && Array.isArray(payload.items) ? payload.items : [];
  if (!rows.length) return roster;
  const fixes = new Map(
    rows
      .map((row) => [String(row.user_id || row.username || "").trim(), row])
      .filter(([id]) => !!id),
  );
  return roster.map((person) => {
    const fix = fixes.get(String(person && person.id || ""));
    if (!fix) return person;
    const lng = Number(fix.longitude ?? fix.lng);
    const lat = Number(fix.latitude ?? fix.lat);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return person;
    return {
      ...person,
      ll: [lng, lat],
      acc: fix.accuracy_m == null ? person.acc : Number(fix.accuracy_m),
      age: fix.age_s == null ? person.age : Number(fix.age_s),
      stale: fix.stale == null ? person.stale : !!fix.stale,
      locationTransport: "sse",
    };
  });
}
const ROLE_GLYPH = { owner:"shield", operator:"user", viewer:"eye", admin:"shield", user:"user" };

/* Two different questions, which used to share one answer and one number.
   SITE_BOUNDS is what must stay on screen. ROAM_BOUNDS is how far the camera
   is allowed to wander. Handing the first to setMaxBounds meant that at the
   minimum zoom the viewport was already wider than the fence, and MapLibre
   answers that by pinning the centre — the map could be zoomed and it could
   not be dragged at all. Keeping the two apart is what makes it draggable
   while still refusing to let anyone wander off to another city. */
function extentOf(devices, fences, people) {
  const pts = [
    ...(devices || []).map((d) => d.ll).filter(Boolean),
    ...Object.values(fences || {}).flat(),
    ...(people || []).map((p) => p.ll).filter(Boolean),
  ];
  if (!pts.length) {
    return { lo: [CENTRE[0] - 0.02, CENTRE[1] - 0.02], hi: [CENTRE[0] + 0.02, CENTRE[1] + 0.02] };
  }
  return {
    lo: [Math.min(...pts.map((p) => p[0])), Math.min(...pts.map((p) => p[1]))],
    hi: [Math.max(...pts.map((p) => p[0])), Math.max(...pts.map((p) => p[1]))],
  };
}
/* Padded by the larger axis so a wide site does not get a letterboxed fence.
   The boundaries are surveyed now rather than compiled in, so the roam limit
   is recomputed when they arrive — a tenant whose site is nowhere near the
   fallback centre used to be locked out of its own map by bounds derived from
   somebody else's fixture. */
function _boundsFor(extent) {
  const span = Math.max(extent.hi[0]-extent.lo[0], extent.hi[1]-extent.lo[1]) || 0.02;
  const pad = f => [[extent.lo[0]-span*f, extent.lo[1]-span*f],
                    [extent.hi[0]+span*f, extent.hi[1]+span*f]];
  return { site: pad(0.16), roam: pad(1.6) };
}

/**
 * Free site roam — multi-country allowed.
 * Soft jail (minZoom ≥12.4 + maxBounds = site·1.6) removed: operators could not
 * zoom out to see neighbouring nations and the camera thrashed when devices landed.
 * Soft≠Live: still clamp tile LODs via OPS_MAP_* — never invent max-bounds theatre.
 */
function fenceSite(m, _extent) {
  if (!m) return;
  try {
    m.setMaxBounds(null);
  } catch {
    /* */
  }
  try {
    m.setMinZoom(OPS_MAP_MIN_ZOOM);
  } catch {
    /* */
  }
  try {
    m.setMaxZoom(OPS_MAP_MAX_ZOOM_SATELLITE);
  } catch {
    /* */
  }
}

/* Night ground. Dark Matter paints wood, grass, parks and neighbourhoods the
   same black as the background, so the tile detail is there and invisible.
   Asphalt land, ink water, olive cover, stone roads. Gold stays on the focus
   boundary, not on every street. Vector tiles still stop at zoom 14. */
const GROUND = "#16130F";
const STONE = ["match", ["get", "class"], "wood", "#243028", "grass", "#31402A", "#2A3828"];
/* Street photograph fades in only once the vector tiles have given all they have. */
const SAT_STREET = ["interpolate", ["linear"], ["zoom"], 15, 0, 15.8, 0.55, 16.8, 0.82, 18, 0.9];
const GROUND_OUT = ["interpolate", ["linear"], ["zoom"], 15.2, 1, 16.6, 0.06];

function paintProp(m, id, key, value) {
  if (!m.getLayer(id)) return;
  try { m.setPaintProperty(id, key, value); } catch { /* layer lacks this paint key */ }
}

function dressOpsGround(m) {
  paintProp(m, "background", "background-color", GROUND);
  paintProp(m, "landcover", "fill-color", STONE);
  paintProp(m, "landcover", "fill-opacity", GROUND_OUT);
  paintProp(m, "park_national_park", "fill-color", "#1C2A22");
  paintProp(m, "park_national_park", "fill-opacity", GROUND_OUT);
  paintProp(m, "park_nature_reserve", "fill-color", "#1C2A22");
  paintProp(m, "park_nature_reserve", "fill-opacity", GROUND_OUT);
  paintProp(m, "landuse_residential", "fill-color", "#211C17");
  paintProp(m, "landuse_residential", "fill-opacity", GROUND_OUT);
  paintProp(m, "landuse", "fill-color", "#2A2620");
  paintProp(m, "landuse", "fill-opacity", GROUND_OUT);
  paintProp(m, "water", "fill-color", "#1B3038");
  paintProp(m, "water", "fill-opacity", GROUND_OUT);
  paintProp(m, "water_shadow", "fill-color", "#10181C");
  paintProp(m, "water_shadow", "fill-opacity", GROUND_OUT);
  paintProp(m, "waterway", "line-color", "#4A7A86");
  paintProp(m, "waterway", "line-width", ["interpolate", ["linear"], ["zoom"], 8, 0.6, 12, 1.4, 16, 3.2]);
  paintProp(m, "boundary_country_inner", "line-color", "#8A7B66");
  paintProp(m, "boundary_country_inner", "line-width", ["interpolate", ["linear"], ["zoom"], 3, 1.1, 6, 1.8]);
  paintProp(m, "boundary_country_outline", "line-color", "#3A342C");
  paintProp(m, "boundary_state", "line-color", "#4A4338");
  paintProp(m, "boundary_county", "line-color", "#3A342C");
  paintProp(m, "aeroway-runway", "line-color", "#5C5346");
  paintProp(m, "aeroway-taxiway", "line-color", "#3A342C");
  paintProp(m, "building", "fill-opacity", 0);
  paintProp(m, "building-top", "fill-opacity", 0);
  const src = m.getSource("carto") ? "carto" : null;
  if (!src) return;
  const before = m.getLayer("waterway") ? "waterway" : undefined;
  const plates = [
    ["ops-cover-wetland", "landcover", ["wetland"], "#163038"],
    ["ops-cover-farm", "landcover", ["farmland"], "#2A261C"],
    ["ops-cover-sand", "landcover", ["sand"], "#3A3228"],
    ["ops-use-commercial", "landuse", ["commercial", "retail"], "#2A241C"],
    ["ops-use-industrial", "landuse", ["industrial", "railway", "garages"], "#302820"],
    ["ops-use-civic", "landuse", ["school", "university", "college", "hospital", "kindergarten"], "#243028"],
    ["ops-use-pitch", "landuse", ["pitch", "playground"], "#243228"],
  ];
  for (const [id, sourceLayer, classes, color] of plates) {
    if (m.getLayer(id)) continue;
    try {
      const layer = {
        id, type: "fill", source: src, "source-layer": sourceLayer,
        filter: ["match", ["get", "class"], classes, true, false],
        paint: { "fill-color": color, "fill-opacity": GROUND_OUT },
      };
      if (before) m.addLayer(layer, before);
      else m.addLayer(layer);
    } catch { /* source has no such layer */ }
  }
}

/* Measured footprints. Height is the tile's render_height.
   Daytime facade colours are not used; they wash out on this ground. */
function addBuildings(m){
  const src = Object.entries(m.getStyle().sources)
    .find(([,s])=>s.type==="vector" && /carto|openmaptiles/i.test(JSON.stringify(s)));
  if(!src) return;
  const before = m.getStyle().layers.find(l=>l.type==="symbol")?.id;
  const height = ["coalesce",["get","render_height"],["get","height"],6];
  /* Daytime facade colours wash out under the night ground, so volume uses stone. */
  const stone = ["interpolate",["linear"],height,
    0,"#241F1A", 6,"#322C26", 18,"#4A4338", 40,"#6A5E50", 80,"#8A7B66", 160,"#C4B49A", 240,"#E7D7B8"];
  const add = (layer) => {
    try {
      if (before) m.addLayer(layer, before);
      else m.addLayer(layer);
    } catch { /* style not ready */ }
  };
  add({
    id:"bld-3d", type:"fill-extrusion", source:src[0], "source-layer":"building",
    minzoom:12.4,
    filter:["all", ["!=", ["get", "hide_3d"], 1], ["!=", ["to-string", ["get", "hide_3d"]], "true"]],
    paint:{
      "fill-extrusion-color": stone,
      "fill-extrusion-height":["interpolate",["linear"],["zoom"],
        12.6,0, 14.2,height],
      "fill-extrusion-base":["coalesce",["get","render_min_height"],0],
      "fill-extrusion-opacity":1,
      "fill-extrusion-vertical-gradient":true,
    },
  });
  const footprint = ["match", ["geometry-type"], ["Polygon", "MultiPolygon"], true, false];
  const standing = ["all",
    ["!=", ["get", "hide_3d"], 1],
    ["!=", ["to-string", ["get", "hide_3d"]], "true"],
    [">", ["to-number", ["coalesce", ["get", "render_height"], ["get", "height"], 0]], 16],
    ["<", ["to-number", ["coalesce", ["get", "render_height"], ["get", "height"], 0]], 48]];
  add({
    id:"bld-roof", type:"fill-extrusion", source:src[0], "source-layer":"building",
    minzoom:14.8,
    filter: standing,
    paint:{
      "fill-extrusion-color":"#5C5346",
      "fill-extrusion-height": height,
      "fill-extrusion-base":["max", 0, ["-", height, 1.4]],
      "fill-extrusion-opacity":1,
      "fill-extrusion-vertical-gradient":false,
    },
  });
  add({
    id:"bld-cut", type:"line", source:src[0], "source-layer":"building",
    minzoom:14.2,
    filter: footprint,
    paint:{
      "line-color":"#100E0C",
      "line-opacity":0.95,
      "line-width":["interpolate",["linear"],["zoom"], 14.2,0.9, 17,2, 19,2.6],
    },
  });
  add({
    id:"bld-edge", type:"line", source:src[0], "source-layer":"building",
    minzoom:14.2,
    filter: footprint,
    paint:{
      "line-color":"#8A7B66",
      "line-opacity":0.9,
      "line-width":["interpolate",["linear"],["zoom"], 14.2,0.4, 17,1.15, 19,1.7],
    },
  });
}

function showBuildings(m, vis) {
  for (const id of ["bld-3d", "bld-roof", "bld-cut", "bld-edge"]) {
    try { if (m.getLayer(id)) m.setLayoutProperty(id, "visibility", vis); } catch { /* */ }
  }
}

/* CARTO's dark labels sit at the edge of legibility. Operators read street and
   place names off this map, so they are lifted and given a halo to sit in.   */
/* The base style ships road names at a flat 9px and retires every place label
   between z14 and z16. Zooming in therefore made the map *less* readable: the
   names you navigate by disappeared exactly as you approached them, and what
   survived was 9px type on a dark plate. Both halves are corrected here — type
   scales with zoom the way the ground does, and no label layer retires while
   the operator is still zooming toward it. */
/* One cycle of a travelling dash, precomputed. Stepping through these moves the
   gap along the line, which is the only way to march a boundary in MapLibre. */
const DASH = [[0,4,3],[0.5,4,2.5],[1,4,2],[1.5,4,1.5],[2,4,1],[2.5,4,0.5],[3,4,0],
              [0,0.5,3,3.5],[0,1,3,3],[0,1.5,3,2.5],[0,2,3,2],[0,2.5,3,1.5],[0,3,3,1]];

const SZ_ROAD  = ["interpolate",["linear"],["zoom"], 12,11, 14,12.5, 16,14.5, 18,16.5, 20,18];
/* Places keep growing but flatten off: past a neighbourhood view the district
   name is context, not the subject, and a 23px suburb label would outrank the
   street the operator is actually reading. */
const SZ_PLACE = ["interpolate",["linear"],["zoom"], 3,12, 8,14, 11,15.5, 14,17, 17,18, 20,19];
const SZ_POI   = ["interpolate",["linear"],["zoom"], 15,10.5, 17,12, 19,13.5];
const POI_IN   = ["interpolate",["linear"],["zoom"], 14.6,0, 15.6,0.45, 16.8,1];

function brightenLabels(m){
  m.getStyle().layers.filter(l=>l.type==="symbol").forEach(l=>{
    const road  = /road|street|transportation|highway/i.test(l.id);
    /* The `_dot_` tiers are the low-zoom dot-plus-name renderings; they hand off
       to the ranked tiers and must keep their original ranges or cities double up. */
    const place = /place|city|town|village|country|state/i.test(l.id) && !/_dot_/i.test(l.id);
    const house = /housenum/i.test(l.id);
    const poi   = /poi|building/i.test(l.id) && !house;
    const water = /water|marine|river/i.test(l.id);
    try{
      m.setPaintProperty(l.id,"text-color",
        place ? "#F4EFE6" : water ? "#9BB8B0" : road ? "#E7E1D4" : "#D7E3D4");
      /* Labels sit over asphalt *and* over the globe photograph,
         so the halo has to carry them on both. Tight and opaque, not soft. */
      m.setPaintProperty(l.id,"text-halo-color","#100E0C");
      m.setPaintProperty(l.id,"text-halo-width", road||place ? 1.7 : 1.3);
      m.setPaintProperty(l.id,"text-halo-blur",.3);
      m.setPaintProperty(l.id,"text-opacity", poi ? POI_IN : 1);
      if (poi) {
        try { m.setPaintProperty(l.id, "icon-opacity", POI_IN); } catch { /* no icon */ }
      }
      if(road)      m.setLayoutProperty(l.id,"text-size",SZ_ROAD);
      else if(place)m.setLayoutProperty(l.id,"text-size",SZ_PLACE);
      else if(poi || house) m.setLayoutProperty(l.id,"text-size",SZ_POI);
      if((place||poi||house) && l.maxzoom!=null && l.maxzoom<24)
        m.setLayerZoomRange(l.id, l.minzoom==null?0:l.minzoom, 24);
      if(house)
        m.setLayerZoomRange(l.id, 17.2, 24);
    }catch{}
  });
}

/* The globe wears the daylight photograph. On the site map the same tiles
   sit under the blocks, darkened, and only after zoom 15 — that is where the
   vector tiles stop and roofs, trees and yards actually exist. */
function addSatellite(m){
  try{
    m.addSource("sat",{ type:"raster", tileSize:256, maxzoom:19,
      tiles:["https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"],
      attribution:"Esri · Maxar · Earthstar Geographics" });
    m.addLayer({ id:"sat", type:"raster", source:"sat",
      paint:{ "raster-opacity": SAT_STREET, "raster-opacity-transition":{ duration:600 },
              "raster-saturation": -0.72, "raster-contrast": 0.3,
              "raster-brightness-min": 0, "raster-brightness-max": 0.4 } });
    seatSatellite(m, "street");
  }catch{}
}

function seatSatellite(m, mode) {
  if (!m || !m.getLayer("sat")) return;
  if (mode === "globe") {
    try { m.moveLayer("sat"); } catch { /* already on top */ }
    try {
      m.setPaintProperty("sat", "raster-opacity", 1);
      m.setPaintProperty("sat", "raster-saturation", -0.18);
      m.setPaintProperty("sat", "raster-contrast", 0.12);
      m.setPaintProperty("sat", "raster-brightness-max", 0.94);
    } catch { /* */ }
    return;
  }
  const before = m.getLayer("landcover") ? "landcover" : (m.getLayer("water") ? "water" : undefined);
  try { if (before) m.moveLayer("sat", before); } catch { /* */ }
  try {
    m.setPaintProperty("sat", "raster-opacity", SAT_STREET);
    m.setPaintProperty("sat", "raster-saturation", -0.72);
    m.setPaintProperty("sat", "raster-contrast", 0.3);
    m.setPaintProperty("sat", "raster-brightness-min", 0);
    m.setPaintProperty("sat", "raster-brightness-max", 0.4);
  } catch { /* */ }
}

/* ═══════════════════ map ═══════════════════════════════════════════════ */
function MapCanvas({ selected, onSelect, layers, setNodes, flyKey, onPanel, panelOpen,
                     view, setView, person, peopleLive, softGeo, toast, seat, softOps,
                     activeZoneId, onZoneEnter, softFleetState, sidePanelOpen,
                     registryItems, regionIds, siteIds, focus, onFocus, focusOpen, fill }){
  const host = useRef(null), map = useRef(null), pool = useRef({});
  const softPool = useRef({});
  const thumbPool = useRef(new Map());
  const previewSubRef = useRef(null);
  const [previewTickets, setPreviewTickets] = useState({});
  const [aiLiveSeats, setAiLiveSeats] = useState([]);
  const [aiLoop, setAiLoop] = useState(null);
  const [ready,setReady] = useState(false);
  const [bindTick, setBindTick] = useState(0);
  const spin = useRef(null);
  const site = useSite();
  const centre = site.centre;
  const people = peopleLive || site.people || [];
  const zones = site.zones || [];
  /* Region filter: zero regions = whole map; else only picked zones paint. */
  const regionKey = Array.isArray(regionIds) ? regionIds.join("|") : "";
  const region = useMemo(
    () => regionMatcherOf(regionKey ? regionKey.split("|") : [], zones),
    [regionKey, zones],
  );
  const siteKey = Array.isArray(siteIds) ? siteIds.join("|") : "";
  const siteWant = useMemo(() => (siteKey ? siteKey.split("|") : []), [siteKey]);
  const devices = useMemo(
    () => (site.devices || []).filter((d) => deviceMatchesSites(d, siteWant, zones) && region.device(d) && pointInFocus(d.ll, focus)),
    [site.devices, region, siteWant, zones, focus],
  );
  const fences = useMemo(() => {
    const all = site.fences || {};
    const out = {};
    for (const [name, ring] of Object.entries(all)) {
      if (!region.zone(name)) continue;
      if (!fenceMatchesSites(name, siteWant, zones)) continue;
      if (!ringInFocus(ring, focus)) continue;
      out[name] = ring;
    }
    return out;
  }, [site.fences, region, siteWant, zones, focus]);
  const extent = useMemo(() => extentOf(devices, fences, people), [devices, fences, people]);
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const focusFlown = useRef("");
  const focusPrior = useRef(null);
  const focusKey = focusPlaceKey(focus);
  const focusKeyRef = useRef("");
  focusKeyRef.current = focusKey;
  useEffect(() => {
    const m = map.current;
    if (!m || !ready) return undefined;
    const paint = () => {
      try { applyMapFocus(m, focus); } catch { /* style not ready */ }
    };
    if (m.isStyleLoaded && m.isStyleLoaded()) paint();
    else m.once("load", paint);
    const cam = focusCamera(focus);
    if (cam && focusKey && focusKey !== focusFlown.current) {
      if (!focusPrior.current) {
        try {
          const c = m.getCenter();
          focusPrior.current = {
            center: [c.lng, c.lat],
            zoom: m.getZoom(),
            pitch: m.getPitch(),
            bearing: m.getBearing(),
          };
        } catch { /* map gone */ }
      }
      focusFlown.current = focusKey;
      try {
        m.fitBounds(cam.bounds, { padding: 72, pitch: cam.pitch, bearing: 0, duration: 700, maxZoom: cam.maxZoom });
      } catch { /* map gone */ }
    }
    if (!focusKey) {
      focusFlown.current = "";
      const back = focusPrior.current;
      focusPrior.current = null;
      if (back) {
        try { m.easeTo({ ...back, duration: 640, essential: true }); } catch { /* map gone */ }
      }
    }
    return undefined;
  }, [focus, focusKey, ready]);
  useEffect(() => {
    const m = map.current;
    if (!m || !ready) return undefined;
    const raf = requestAnimationFrame(() => {
      try { m.resize(); } catch { /* map gone */ }
    });
    return () => cancelAnimationFrame(raf);
  }, [fill, ready]);
  /* The map is created once, but the extent arrives later and changes again as
     boundaries and people load. A ref keeps the setup effect honest without
     tearing the map down every time a person moves. */
  const extentRef = useRef(extent);
  extentRef.current = extent;
  /* Only read while the trail layer is actually on. See `useTrack`. */
  const mapTrack = useTrack(person, layers.trail).pts;
  const [mapBoot, setMapBoot] = useState(0);
  const previewSourceKey = useMemo(
    () => livePreviewSourceIdsOf(registryItems, 4).join("|"),
    [registryItems],
  );

  useEffect(()=>{
    const el = host.current;
    if (!el) return undefined;
    /* Zero-size host is common on first paint (Safari / nested min-height). */
    if (!opsMapContainerOk(el).ok) {
      let n = 0;
      const t = setInterval(() => {
        n += 1;
        if (opsMapContainerOk(el).ok || n >= 50) {
          clearInterval(t);
          setMapBoot((k) => k + 1);
        }
      }, 40);
      return () => clearInterval(t);
    }
    const m = new maplibregl.Map({
      container: el,
      style:"https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
      center: centre,
      zoom: OPS_MAP_DEFAULT_ZOOM_REGION,
      pitch: 0,
      bearing: 0,
      attributionControl:{ compact:true },
      minZoom: OPS_MAP_MIN_ZOOM,
      maxZoom: OPS_MAP_MAX_ZOOM_SATELLITE,
      maxPitch:72,
      pixelRatio: opsMapPixelRatio(),
      // Cross-browser: keep rendering even if software WebGL (VMs / older Edge).
      failIfMajorPerformanceCaveat: false,
      trackResize: true,
      crossSourceCollisions: false,
      // Unbounded world: horizontal wrap · fenceSite keeps setMaxBounds(null).
      renderWorldCopies: true,
      // Fluency Soft invent Soft invent Soft invent Soft no detail 降级 — more tile cache/parallel.
      ...opsMapFluencyConstructOpts(),
    });
    try {
      fenceSite(m, extentRef.current);
    } catch {
      /* */
    }
    map.current = m;
    window.__map = m;
    try {
      m.__opsFluencyUnbind = installOpsMapFluency(m);
    } catch {
      m.__opsFluencyUnbind = null;
    }
    m.on("error", (ev) => {
      try {
        const msg = String(
          (ev && ev.error && (ev.error.message || ev.error)) ||
            (ev && ev.message) ||
            "",
        );
        if (!msg || m.__opsMapErrToast) return;
        if (/webgl|Failed to initialize|context/i.test(msg)) {
          m.__opsMapErrToast = true;
          toastRef.current &&
            toastRef.current("Map needs WebGL — try Chrome / Edge, or enable hardware acceleration", "bad");
        } else if (/Failed to fetch|network|CORS|style/i.test(msg)) {
          m.__opsMapErrToast = true;
          toastRef.current &&
            toastRef.current("Map basemap blocked — check network / ad-block for cartocdn tiles", "warn");
        }
      } catch {
        /* */
      }
    });
    m.on("load", ()=>{
      syncOpsMapViewport(m, el, { view: "site" });
      dressOpsGround(m);
      addBuildings(m);
      addSatellite(m);
      brightenLabels(m);
      installOpsRoadGraph(m);
      /* Empty until the surveyed boundaries arrive. A tenant that has drawn no
         boundary sees none, rather than somebody else's perimeter. */
      m.addSource("fence",{
        type:"geojson",
        data:{ type:"FeatureCollection", features:[] },
        promoteId: "zoneId",
      });
      m.addLayer({ id:"fence-fill", type:"fill", source:"fence",
        paint:{
          "fill-color": fenceFillColorExpr(),
          "fill-opacity":[
            "case",
            ["boolean",["feature-state","hover"],false],0.32,
            ["==",["get","active"],1],0.26,
            ["==",["get","soft"],1],0.09,
            ["*", ["number", ["feature-state", "pulse"], 0.45], 0.22],
          ],
        } });
      m.addLayer({ id:"fence-line", type:"line", source:"fence",
        paint:{
          "line-color": fenceLineColorExpr(),
          "line-opacity":[
            "case",
            ["boolean",["feature-state","hover"],false],0.98,
            ["==",["get","active"],1],0.95,
            ["number", ["feature-state", "pulse"], 0.55],
          ],
          "line-width":[
            "case",
            ["==",["get","active"],1],2.8,
            ["boolean",["feature-state","hover"],false],2.2,
            ["==",["get","soft"],1],1.6,
            1.6
          ],
          "line-dasharray":[3,2.4],
        } });
      /* Signature Orchestra: harmonic ribbon + marching pulse — Soft pads excluded. */
      m.addLayer({ id:"fence-ribbon", type:"line", source:"fence",
        filter:["==",["get","soft"],0],
        paint:{
          "line-color":["get","harmonic"],
          "line-opacity":["*", ["number",["feature-state","rib"],0.45], 0.55],
          "line-width":[
            "case",
            ["==",["get","active"],1],6.5,
            ["+", 3.8, ["*", ["number",["feature-state","rib"],0.4], 2.4]]
          ],
          "line-blur":1.1,
        } });
      m.addLayer({ id:"fence-pulse", type:"line", source:"fence",
        filter:["==",["get","soft"],0],
        paint:{
          "line-color":["get","color"],
          "line-opacity":["number",["feature-state","pulse"],0.55],
          "line-width":[
            "case",
            ["==",["get","active"],1],3.6,
            2.4
          ],
          "line-dasharray":[1.4, 0.7, 2.2, 0.9],
        } });
      m.addSource("zone-beacons",{ type:"geojson", data:{ type:"FeatureCollection", features:[] } });
      m.addLayer({ id:"zone-beacon-halo", type:"circle", source:"zone-beacons",
        paint:{
          "circle-radius":14,
          "circle-color":["get","color"],
          "circle-opacity":0.16,
          "circle-blur":0.7,
        } });
      m.addLayer({ id:"zone-beacon-core", type:"circle", source:"zone-beacons",
        paint:{
          "circle-radius":4.2,
          "circle-color":["get","harmonic"],
          "circle-opacity":0.95,
          "circle-stroke-width":1.8,
          "circle-stroke-color":["get","color"],
        } });
      m.addLayer({ id:"fence-label", type:"symbol", source:"fence",
        layout:{
          "text-field":["get","name"],
          "text-size":11,
          "text-offset":[0,0.15],
          "text-anchor":"center",
          "text-allow-overlap":false,
          "symbol-placement":"point",
        },
        paint:{
          "text-color":[
            "case",
            ["==",["get","active"],1],"#E9EFEE",
            ["==",["get","soft"],1],"#FFD9A8",
            ["get","color"]
          ],
          "text-halo-color":"rgba(4,8,10,.88)",
          "text-halo-width":1.4,
          "text-opacity":0.92,
        } });
      /* A zone with something open does not get a brighter version of the calm
         boundary — it gets a different one. The dash travels, which no quiet
         zone ever does, so "which area is live" survives peripheral vision. */
      m.addLayer({ id:"fence-alert", type:"line", source:"fence",
        filter:["in","name",""],
        paint:{ "line-color":"#FFB65C","line-opacity":.92,"line-width":2.4,"line-dasharray":[0,4,3] } });
      m.addLayer({ id:"fence-alert-glow", type:"fill", source:"fence",
        filter:["in","name",""],
        paint:{ "fill-color":"#FFB65C","fill-opacity":.09 } });
      /* Focus volume ring — distinct from alarm fence-alert. Soft≠Live: focus is space, not incident. */
      m.addLayer({ id:"fence-focus", type:"line", source:"fence",
        filter:["==",["get","active"],1],
        paint:{
          "line-color":"#c8e8ff",
          "line-opacity":0.98,
          "line-width":3.4,
          "line-dasharray":[0,4,3],
        } });
      m.addLayer({ id:"fence-focus-glow", type:"fill", source:"fence",
        filter:["==",["get","active"],1],
        paint:{
          "fill-color":"#8fd0ff",
          "fill-opacity":0.12,
        } });
      m.addSource("mesh",{ type:"geojson", data:{ type:"FeatureCollection", features:[] } });
      m.addLayer({ id:"mesh-line", type:"line", source:"mesh",
        paint:{ "line-color":"#E9EFEE","line-opacity":.075,"line-width":1 } });
      m.addSource("trail",{ type:"geojson", data:{ type:"FeatureCollection", features:[] } });
      m.addLayer({ id:"trail-line", type:"line", source:"trail",
        layout:{ "line-cap":"round","line-join":"round" },
        paint:{ "line-color":"#A78BFA","line-opacity":.55,"line-width":2.2,"line-dasharray":[1,1.7] } });
      m.addLayer({ id:"trail-pt", type:"circle", source:"trail", filter:["==","$type","Point"],
        paint:{ "circle-radius":3,"circle-color":"#0B1114","circle-stroke-color":"#A78BFA",
                "circle-stroke-width":1.4,"circle-opacity":.9 } });
      /* Soft fleet — skill routes: road OSM vs air corridor. Soft≠Live. */
      m.addSource("soft-routes",{ type:"geojson", data:{ type:"FeatureCollection", features:[] } });
      m.addLayer({ id:"soft-routes-glow", type:"line", source:"soft-routes",
        layout:{ "line-cap":"round","line-join":"round" },
        paint:{ "line-color":["get","color"],"line-opacity":.42,"line-width":14 } });
      m.addLayer({ id:"soft-routes-walk", type:"line", source:"soft-routes",
        filter:["==",["get","profile"],"walk"],
        layout:{ "line-cap":"round","line-join":"round" },
        paint:{ "line-color":["get","color"],"line-opacity":1,"line-width":4.2,
                "line-dasharray":[1.6,1.2] } });
      m.addLayer({ id:"soft-routes-bike", type:"line", source:"soft-routes",
        filter:["any",["==",["get","profile"],"bike"],["==",["get","profile"],"drive"]],
        layout:{ "line-cap":"round","line-join":"round" },
        paint:{ "line-color":["get","color"],"line-opacity":1,"line-width":4.6,
                "line-dasharray":[2.4,0.8] } });
      m.addLayer({ id:"soft-routes-air", type:"line", source:"soft-routes",
        filter:["==",["get","profile"],"air"],
        layout:{ "line-cap":"round","line-join":"round" },
        paint:{ "line-color":["get","color"],"line-opacity":1,"line-width":3.4,
                "line-dasharray":[0.8,1.6] } });
      /* Halo source kept empty — filled discs under units looked like drift.
         Soft identity is 100% DOM pins. Incident ring: stroke only. */
      m.addSource("soft-assets",{ type:"geojson", data:{ type:"FeatureCollection", features:[] } });
      m.addLayer({ id:"soft-assets-halo", type:"circle", source:"soft-assets",
        paint:{
          "circle-radius":1,
          "circle-opacity":0,
          "circle-stroke-width":0,
        } });
      /* soft-incidents kept for API compat; ring paint off — empty stroke rings read as “4 hollow circles”. */
      m.addSource("soft-incidents",{ type:"geojson", data:{ type:"FeatureCollection", features:[] } });
      m.addLayer({ id:"soft-inc-ring", type:"circle", source:"soft-incidents",
        paint:{
          "circle-radius":1,
          "circle-color":"transparent",
          "circle-opacity":0,
          "circle-stroke-width":0,
          "circle-stroke-opacity":0,
        } });
      /* Cortex Resolve — arcs, cones, constellation, flow sparks. Soft≠Live. */
      m.addSource("ai-sim-signals",{ type:"geojson", data:{ type:"FeatureCollection", features:[] } });
      m.addLayer({ id:"ai-sim-glow", type:"circle", source:"ai-sim-signals",
        paint:{
          "circle-radius":22,
          "circle-color":[
            "match",["get","kind"],
            "alarm","#FF6B5A",
            "observe","#7CFFB2",
            "#6ED8FF"
          ],
          "circle-opacity":0.2,
          "circle-blur":0.65,
        } });
      m.addLayer({ id:"ai-sim-core", type:"circle", source:"ai-sim-signals",
        paint:{
          "circle-radius":5.2,
          "circle-color":[
            "match",["get","kind"],
            "alarm","#FF6B5A",
            "observe","#7CFFB2",
            "#B8F0FF"
          ],
          "circle-opacity":0.96,
          "circle-stroke-width":1.8,
          "circle-stroke-color":"#0B1216",
        } });
      m.addSource("ai-attention-cones",{ type:"geojson", data:{ type:"FeatureCollection", features:[] } });
      m.addLayer({ id:"ai-cone-fill", type:"fill", source:"ai-attention-cones",
        paint:{
          "fill-color":["get","color"],
          "fill-opacity":0.11,
        } });
      m.addLayer({ id:"ai-cone-line", type:"line", source:"ai-attention-cones",
        paint:{
          "line-color":["get","color"],
          "line-opacity":0.45,
          "line-width":1.2,
        } });
      m.addSource("ai-constellation",{ type:"geojson", data:{ type:"FeatureCollection", features:[] } });
      m.addLayer({ id:"ai-graph-line", type:"line", source:"ai-constellation",
        layout:{ "line-cap":"round" },
        paint:{
          "line-color":["get","color"],
          "line-opacity":0.28,
          "line-width":1.1,
          "line-dasharray":[0.4, 1.6],
        } });
      m.addSource("ai-dispatch-routes",{ type:"geojson", data:{ type:"FeatureCollection", features:[] } });
      m.addLayer({ id:"ai-dispatch-glow", type:"line", source:"ai-dispatch-routes",
        layout:{ "line-cap":"round","line-join":"round" },
        paint:{ "line-color":["get","color"],"line-opacity":0.28,"line-width":12,"line-blur":1.2 } });
      m.addLayer({ id:"ai-dispatch-line", type:"line", source:"ai-dispatch-routes",
        layout:{ "line-cap":"round","line-join":"round" },
        paint:{
          "line-color":["get","color"],
          "line-opacity":0.95,
          "line-width":[
            "case",
            ["==",["get","rank"],1],4.2,
            ["==",["get","rank"],2],3.2,
            2.4
          ],
        } });
      m.addSource("ai-flow-sparks",{ type:"geojson", data:{ type:"FeatureCollection", features:[] } });
      m.addLayer({ id:"ai-spark-glow", type:"circle", source:"ai-flow-sparks",
        paint:{
          "circle-radius":7,
          "circle-color":["get","color"],
          "circle-opacity":0.35,
          "circle-blur":0.6,
        } });
      m.addLayer({ id:"ai-spark-core", type:"circle", source:"ai-flow-sparks",
        paint:{
          "circle-radius":2.6,
          "circle-color":"#F8FFFE",
          "circle-opacity":0.95,
          "circle-stroke-width":1.2,
          "circle-stroke-color":["get","color"],
        } });
      fenceSite(m, extentRef.current);
      m.easeTo({ center:centre, duration:0 });
      setReady(true);
    });
    /* Page78 Soft≠Live: right-click stores Live assign pin for External / SEB.
       Never invent Soft unit-moved — External desk still needs Edge ack+receipt. */
    m.on("contextmenu", (e) => {
      try {
        if (e && e.originalEvent && typeof e.originalEvent.preventDefault === "function") {
          e.originalEvent.preventDefault();
        }
      } catch (_) { /* ignore */ }
      const pin = mapEventPinOf(e);
      assignExternalPinLive(pin, toastRef.current, "right-click");
    });
    /* Viewport: window + host ResizeObserver + panel open/close. Hard-coded
       right:420 crushed laptop widths and left Safari stuck after dock changes. */
    let raf = 0;
    const refit = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const mm = map.current;
        if (!mm) return;
        syncOpsMapViewport(mm, el, { view: "site" });
        try {
          if (mm.getMaxBounds && mm.getMaxBounds()) fenceSite(mm, extentRef.current);
        } catch {
          /* */
        }
      });
    };
    addEventListener("resize", refit);
    addEventListener("orientationchange", refit);
    let ro = null;
    try {
      if (typeof ResizeObserver === "function") {
        ro = new ResizeObserver(refit);
        ro.observe(el);
        const shell = el.closest && el.closest(".ops-map-shell");
        if (shell) ro.observe(shell);
        const page = el.closest && el.closest(".page");
        if (page) ro.observe(page);
      }
    } catch {
      ro = null;
    }
    /* Panel animates width after open — remeasure a few ticks. */
    const t1 = setTimeout(refit, 80);
    const t2 = setTimeout(refit, 320);
    const t3 = setTimeout(refit, 900);
    return () => {
      removeEventListener("resize", refit);
      removeEventListener("orientationchange", refit);
      if (ro) try { ro.disconnect(); } catch { /* */ }
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      if (raf) cancelAnimationFrame(raf);
      try {
        if (typeof m.__opsFluencyUnbind === "function") m.__opsFluencyUnbind();
      } catch {
        /* */
      }
      m.remove();
      map.current = null;
    };
  },[mapBoot]);

  /* Boundaries follow the survey · Soft pads add zone rings on 127 only. */
  useEffect(()=>{
    const m = map.current; if(!m || !ready) return;
    const src = m.getSource("fence"); if(!src) return;
    const active = String(activeZoneId || "").trim();
    const softOn = isLocalHost() && !!(softFleetState);
    const cat = softZoneCatalogOf({
      zones,
      devices,
      softState: softOn ? softFleetState : null,
      centre: site.centre,
    });
    const byKey = new Map();
    for (const z of cat) {
      if (!z) continue;
      byKey.set(String(z.id || ""), z);
      if (z.label) byKey.set(String(z.label), z);
    }
    const feats = Object.entries(fences).map(([name,ring])=>{
      const z = byKey.get(name) || { id: name, label: name, soft: false };
      const vm = softZoneVolumeVmOf(z, softOn ? softFleetState : null, devices);
      const props = enrichFencePulseProps({
        name,
        zoneId: name,
        soft: 0,
        active: active && (active === name) ? 1 : 0,
        ready: vm.ready || "open",
        coverN: vm.coverN || 0,
        tone: vm.tone || "",
      });
      return {
        type:"Feature",
        id: name,
        properties: props,
        geometry:{ type:"Polygon", coordinates:[ring] },
      };
    });
    if (softOn) {
      for (const z of cat) {
        if (!z.soft || !z.ring || z.ring.length < 4) continue;
        if (feats.some((f) => f.properties && (f.properties.zoneId === z.id || f.properties.name === z.label))) continue;
        const vm = softZoneVolumeVmOf(z, softFleetState, devices);
        const props = enrichFencePulseProps({
          name: z.label,
          zoneId: z.id,
          soft: 1,
          active: active && (active === z.id || active === z.label) ? 1 : 0,
          ready: vm.ready || "open",
          coverN: vm.coverN || 0,
          tone: z.tone || vm.tone || "amber",
        });
        feats.push({
          type: "Feature",
          id: z.id,
          properties: props,
          geometry: { type: "Polygon", coordinates: [z.ring] },
        });
      }
    }
    opsMapDeferSourceData(m, "fence", { type:"FeatureCollection", features: feats });
    try {
      opsMapDeferSourceData(m, "zone-beacons", buildZoneBeacons(feats));
    } catch {
      /* */
    }
  },[fences, zones, devices, ready, softFleetState, bindTick, activeZoneId, site.centre]);

  /* Signature Orchestra — per-zone BPM breath + ribbon harmonic. Soft pads excluded. */
  useEffect(()=>{
    const m = map.current; if(!m || !ready) return;
    if (!m.getLayer("fence-pulse")) return;
    const show = !!layers.fence;
    for (const lid of ["fence-pulse", "fence-ribbon", "zone-beacon-halo", "zone-beacon-core"]) {
      if (!m.getLayer(lid)) continue;
      try { m.setLayoutProperty(lid, "visibility", show ? "visible" : "none"); } catch { /* */ }
    }
    if (!show) return;
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const liveMeta = Object.keys(fences || {})
      .map((k) => String(k || "").trim())
      .filter(Boolean)
      .map((id) => ({ id, bpm: enrichFencePulseProps({ zoneId: id, soft: 0 }).bpm }));
    if (!liveMeta.length) return;
    let step = 0, raf = 0, last = 0;
    const tick = (t) => {
      raf = requestAnimationFrame(tick);
      if (opsMapCamYield(m)) return;
      if (t - last < 48) return;
      last = t;
      step = (step + 1) % 240;
      tickZonePulseStates(m, liveMeta, step);
      /* Global dash crawl — per-zone dash baked in props isn't animatable per-feature. */
      try {
        const ph = (step % 12) / 12;
        m.setPaintProperty("fence-pulse", "line-dasharray", [
          1.2 + ph * 0.8,
          0.55,
          2.0 + (1 - ph) * 0.6,
          0.75,
        ]);
      } catch { /* */ }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  },[ready, layers.fence, fences]);

  /* Cortex Resolve — arcs / cones / constellation / flow sparks. Soft≠Live. */
  const ontologyPoll = usePolled("/ops/resource-ontology", 20000);
  const [aiHud, setAiHud] = useState(() => aiLoopSummary(null, null));
  const aiRoutesRef = useRef({ type: "FeatureCollection", features: [] });
  const aiToastKey = useRef("");
  useEffect(()=>{
    const m = map.current; if(!m || !ready) return;
    const sigSrc = m.getSource("ai-sim-signals");
    const rtSrc = m.getSource("ai-dispatch-routes");
    const coneSrc = m.getSource("ai-attention-cones");
    const graphSrc = m.getSource("ai-constellation");
    if (!sigSrc || !rtSrc) return;
    const ontology = ontologyPoll && ontologyPoll.data ? ontologyPoll.data : null;
    const signals = buildAiSimSignals({
      devices,
      alarms: site.alarms || [],
      ontology,
    });
    const pack = buildAiCortexPack({
      devices,
      zones,
      fences,
      personnel: people,
      ontology,
      alarms: site.alarms || [],
      maxRoutes: 4,
    });
    try { opsMapDeferSourceData(m, "ai-sim-signals", signals); } catch { /* */ }
    try { opsMapDeferSourceData(m, "ai-dispatch-routes", pack.routes); } catch { /* */ }
    if (coneSrc) try { opsMapDeferSourceData(m, "ai-attention-cones", pack.cones); } catch { /* */ }
    if (graphSrc) try { opsMapDeferSourceData(m, "ai-constellation", pack.graph); } catch { /* */ }
    aiRoutesRef.current = pack.routes;
    const sum = aiLoopSummary(signals, pack.routes);
    setAiHud(sum);
    const key = `${sum.signals}|${sum.routes}|${sum.stage}`;
    if (sum.routes > 0 && key !== aiToastKey.current && toast) {
      aiToastKey.current = key;
      try {
        toast(`Cortex ranked ${sum.routes} corridor${sum.routes === 1 ? "" : "s"} · sim`, "go");
      } catch { /* */ }
    }
  },[ready, devices, zones, fences, people, site.alarms, ontologyPoll && ontologyPoll.data, toast]);

  /* Flow sparks crawl ranked corridors. */
  useEffect(()=>{
    const m = map.current; if(!m || !ready) return;
    const sparkSrc = m.getSource("ai-flow-sparks");
    if (!sparkSrc) return;
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0, last = 0, phase = 0;
    const tick = (t) => {
      raf = requestAnimationFrame(tick);
      if (opsMapCamYield(m)) return;
      if (t - last < 40) return;
      last = t;
      phase = (phase + 0.018) % 1;
      try {
        sparkSrc.setData(buildAiFlowSparks(aiRoutesRef.current, phase));
      } catch { /* */ }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  },[ready]);

  /* Active volume → plant/Soft pin dim outside radius. Soft≠Live. */
  useEffect(()=>{
    const m = map.current;
    if (!m || !ready) return;
    const active = String(activeZoneId || "").trim();
    if (!active || !isLocalHost() || !softFleetState) {
      m.__softVolFocus = null;
      return;
    }
    const cat = softZoneCatalogOf({
      zones,
      devices,
      softState: softFleetState,
      centre: site.centre,
    });
    const z = cat.find((x) => x.id === active || x.label === active);
    const ctx = softVolumeMapContextOf(z, softFleetState, { devices });
    m.__softVolFocus = ctx.ok
      ? { origin: ctx.origin, radiusM: ctx.radiusM, id: ctx.id }
      : null;
    try {
      if (typeof m.__softResync === "function") m.__softResync();
    } catch {
      /* */
    }
  },[ready, activeZoneId, softFleetState, zones, devices, site.centre, bindTick]);

  /* Click zone polygon → enter zone ops space. Soft≠Live. */
  useEffect(()=>{
    const m = map.current;
    if (!m || !ready || !onZoneEnter) return;
    let hoveredId = null;
    const hit = (e) => {
      try {
        if (e && e.originalEvent && e.originalEvent.defaultPrevented) return;
      } catch {
        /* */
      }
      const f = e && e.features && e.features[0];
      if (!f || !f.properties) return;
      const id = String(f.properties.zoneId || f.properties.name || "").trim();
      if (!id) return;
      try {
        if (e.originalEvent) {
          e.originalEvent.preventDefault();
          e.originalEvent.stopPropagation();
        }
      } catch {
        /* */
      }
      onZoneEnter(id);
    };
    const layersHit = ["fence-fill", "fence-line"];
    for (const lid of layersHit) {
      if (!m.getLayer(lid)) continue;
      m.on("click", lid, hit);
    }
    const enter = (e) => {
      try { m.getCanvas().style.cursor = "pointer"; } catch { /* */ }
      const f = e && e.features && e.features[0];
      const id = f && f.properties
        ? String(f.properties.zoneId || f.id || f.properties.name || "").trim()
        : "";
      if (!id) return;
      if (hoveredId && hoveredId !== id) {
        try { m.setFeatureState({ source: "fence", id: hoveredId }, { hover: false }); } catch { /* */ }
      }
      hoveredId = id;
      try { m.setFeatureState({ source: "fence", id: hoveredId }, { hover: true }); } catch { /* */ }
    };
    const leave = () => {
      try { m.getCanvas().style.cursor = ""; } catch { /* */ }
      if (hoveredId) {
        try { m.setFeatureState({ source: "fence", id: hoveredId }, { hover: false }); } catch { /* */ }
        hoveredId = null;
      }
    };
    if (m.getLayer("fence-fill")) {
      m.on("mouseenter", "fence-fill", enter);
      m.on("mouseleave", "fence-fill", leave);
      m.on("mousemove", "fence-fill", enter);
    }
    return () => {
      for (const lid of layersHit) {
        try { m.off("click", lid, hit); } catch { /* */ }
      }
      try {
        m.off("mouseenter", "fence-fill", enter);
        m.off("mouseleave", "fence-fill", leave);
        m.off("mousemove", "fence-fill", enter);
      } catch { /* */ }
      if (hoveredId) {
        try { m.setFeatureState({ source: "fence", id: hoveredId }, { hover: false }); } catch { /* */ }
      }
    };
  },[ready, onZoneEnter]);

  /* Soft map thumbs · promote to Live only with registry + WHEP ticket. */
  useEffect(()=>{
    const m = map.current;
    if (!m || !ready) return;
    const cat = softZoneCatalogOf({
      zones,
      devices,
      softState: isLocalHost() ? softFleetState : null,
      centre: site.centre,
    });
    const softThumbs = softMapLiveThumbsOf({
      zoneCatalog: cat,
      activeZoneId,
      /* Soft≠Live: Soft plant invent only on 127 Soft≠ leak softFleetState on prod map. */
      softState: isLocalHost() ? softFleetState : null,
      devices,
    });
    /* Production Live: registry + device pins Soft≠ Soft Cover as SoT. */
    const liveThumbs = liveRegistryMapThumbsOf(registryItems, devices, site.centre, people);
    const hosts = mergeMapThumbHosts(liveThumbs, softThumbs).filter(region.thumb);
    const thumbs = applyAiLiveMapSeats(
      enrichMapThumbsWithRegistry(
        hosts,
        registryItems,
        previewTickets,
      ),
      aiLiveSeats,
    );
    const on = layers.fence !== false && view === "site";
    syncOpsMapLiveThumbs(
      m,
      thumbPool.current,
      thumbs,
      maplibregl,
      (camId, zoneId, handoff = {}) => {
        if (zoneId) onZoneEnter && onZoneEnter(zoneId);
        const hit = thumbs.find((row) => row && row.id === camId);
        if (hit && hit.live) {
          toastRef.current && toastRef.current("Live preview · " + camId, "go");
          const q = new URLSearchParams();
          q.set("from", "ops_map");
          q.set("handoff", "1");
          if (handoff.sourceId || hit.sourceId) q.set("source", handoff.sourceId || hit.sourceId);
          if (handoff.deviceId || hit.deviceId) q.set("device", handoff.deviceId || hit.deviceId);
          if (zoneId) q.set("zone", zoneId);
          if (handoff.groupId || hit.groupId) q.set("group", handoff.groupId || hit.groupId);
          try { go("/cameras/live?" + q.toString()); } catch { /* */ }
          return;
        }
        toastRef.current &&
          toastRef.current(
            "Standby · " + camId + " · no live stream",
            "warn",
          );
      },
      on,
    );
  },[ready, zones, devices, people, softFleetState, activeZoneId, layers.fence, view, bindTick, onZoneEnter, registryItems, previewTickets, site.centre, aiLiveSeats, region]);

  /* Mint short-lived preview tickets for live_preview sources in viewport thumbs. */
  useEffect(()=>{
    if (!ready || view !== "site") return undefined;
    let dead = false;
    let renewing = false;
    const mint = async () => {
      if (dead || renewing) return;
      renewing = true;
      const mintIds = previewSourceKey.split("|").filter(Boolean).slice(0, 4);
      if (!mintIds.length) {
        if (!dead) setPreviewTickets({});
        renewing = false;
        return;
      }
      try {
        if (previewSubRef.current) {
          try {
            await api("/map/preview-subscriptions/" + encodeURIComponent(previewSubRef.current), {
              method: "DELETE",
            });
          } catch { /* */ }
          previewSubRef.current = null;
        }
        const res = await api("/map/preview-subscriptions", {
          method: "POST",
          body: { source_ids: mintIds, max_streams: 4 },
        });
        if (dead) {
          if (res && res.sub_id) {
            api("/map/preview-subscriptions/" + encodeURIComponent(res.sub_id), {
              method: "DELETE",
            }).catch(() => {});
          }
          return;
        }
        previewSubRef.current = res && res.sub_id ? String(res.sub_id) : null;
        const by = {};
        for (const row of (res && res.items) || []) {
          if (row && row.source_id) by[String(row.source_id)] = row;
        }
        setPreviewTickets(by);
      } catch {
        if (!dead) setPreviewTickets({});
      } finally {
        renewing = false;
      }
    };
    mint();
    const timer = setInterval(mint, 28000);
    return () => {
      dead = true;
      clearInterval(timer);
      const sub = previewSubRef.current;
      previewSubRef.current = null;
      if (sub) {
        api("/map/preview-subscriptions/" + encodeURIComponent(sub), { method: "DELETE" }).catch(() => {});
      }
    };
  },[ready, view, previewSourceKey]);

  /* localStorage bind changes (Zones desk) → refresh thumbs. Soft 127. */
  useEffect(()=>{
    if (!isLocalHost()) return;
    const bump = () => setBindTick((n) => n + 1);
    const onStorage = (ev) => {
      if (ev && ev.key && ev.key.indexOf("croc_ops_zone") >= 0) bump();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("croc-zone-bind", bump);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("croc-zone-bind", bump);
    };
  },[]);

  useEffect(()=>()=>{
    for (const mk of thumbPool.current.values()) {
      try {
        const hostEl = mk.getElement && mk.getElement();
        const el = hostEl && hostEl.querySelector && hostEl.querySelector(".ops-live-thumb");
        try { if (el && el.__pc) el.__pc.close(); } catch { /* */ }
        try { if (mk.__stopPlayer) mk.__stopPlayer(); } catch { /* */ }
        try { if (mk.__moveRaf) cancelAnimationFrame(mk.__moveRaf); } catch { /* */ }
        mk.remove();
      } catch { /* */ }
    }
    thumbPool.current.clear();
  },[]);

  /* Zone paint is data-driven via feature.properties.active (geojson rebuild). */
  useEffect(()=>{
    /* Keep layer visibility in sync with fence toggle. */
    const m = map.current; if(!m || !ready) return;
    const vis = layers.fence !== false ? "visible" : "none";
    ["fence-fill","fence-line","fence-label","fence-alert","fence-alert-glow","fence-focus","fence-focus-glow"].forEach((id) => {
      try { if (m.getLayer(id)) m.setLayoutProperty(id, "visibility", vis); } catch { /* */ }
    });
  },[ready, layers.fence]);

  /* Group links are one label on one home. A nearest-gateway star is not a group. */
  useEffect(()=>{
    const m = map.current; if(!m || !ready) return;
    const src = m.getSource("mesh"); if(!src) return;
    const sel = deviceSelIdOf(selected) || "";
    const fc = buildGroupLinkFeatures(devices, sel);
    opsMapDeferSourceData(m, "mesh", fc);
    const el = host.current;
    if (el) {
      const sig = [...new Set(fc.features.map((f) => {
        const props = f.properties || {};
        return String(props.site || "") + "\t" + String(props.group || "");
      }))].sort();
      el.setAttribute("data-cmc-group-links", sig.join("|"));
    }
  }, [ready, devices, selected]);

  /* site ⇄ globe — camera jumps only on view mode change (never on centre/extent thrash). */
  const viewPrevRef = useRef(null);
  const centreRef = useRef(centre);
  centreRef.current = centre;
  useEffect(()=>{
    const m = map.current; if(!m || !ready) return;
    clearInterval(spin.current);
    const prev = viewPrevRef.current;
    viewPrevRef.current = view;

    if(view === "globe"){
      try { m.setMaxBounds(null); } catch { /* */ }
      try { m.setMinZoom(0); } catch { /* */ }
      m.setProjection({ type:"globe" });
      m.setPaintProperty("background","background-opacity",0);
      seatSatellite(m, "globe");
      showBuildings(m, "none");
      try{ m.setSky({
        "sky-color":"rgba(3,6,10,0)", "horizon-color":"#4FB6C4", "fog-color":"rgba(3,6,10,0)",
        "sky-horizon-blend":.5, "horizon-fog-blend":.4, "fog-ground-blend":.04,
        "atmosphere-blend":["interpolate",["linear"],["zoom"],0,1,4,.55,7,0] }); }catch{}
      syncOpsMapViewport(m, host.current, { view: "globe" });
      /* Only fly when entering globe (or first globe paint) — not on every re-render. */
      if (prev !== "globe") {
        const c = centreRef.current || centre;
        m.easeTo({
          center: c,
          zoom: 1.4,
          pitch: 0,
          bearing: 0,
          duration: 1100,
          essential: true,
          easing: (t) => 1 - Math.pow(1 - t, 3),
        });
      }

      let held = false, idle = null;
      const grab = ()=>{ held = true; clearTimeout(idle); };
      const release = ()=>{ clearTimeout(idle); idle = setTimeout(()=>{ held = false; }, 4000); };
      m.on("dragstart",grab); m.on("zoomstart",grab);
      m.on("dragend",release); m.on("zoomend",release);
      const t = setTimeout(()=>{
        let prevTs = performance.now();
        const step = now=>{
          spin.current = requestAnimationFrame(step);
          const mm = map.current; if(!mm) return;
          const dt = Math.min(now - prevTs, 64); prevTs = now;
          if(!held && !mm.isMoving() && !opsMapCamYield(mm))
            mm.setCenter([mm.getCenter().lng + dt*0.0011, mm.getCenter().lat]);
        };
        spin.current = requestAnimationFrame(step);
      },1700);
      return ()=>{ clearTimeout(t); clearTimeout(idle); cancelAnimationFrame(spin.current);
        m.off("dragstart",grab); m.off("zoomstart",grab);
        m.off("dragend",release); m.off("zoomend",release); };
    }

    /* Site view */
    m.setPaintProperty("background","background-opacity",1);
    syncOpsMapViewport(m, host.current, { view: "site" });
    seatSatellite(m, "street");
    showBuildings(m, "visible");
    try {
      m.setProjection({ type: "mercator" });
    } catch {
      /* */
    }
    fenceSite(m, extentRef.current);

    /* Only re-frame when switching into site from globe (or first ready).
       A focused board already framed itself; this jump would cover it. */
    if ((prev === "globe" || prev == null) && !focusKeyRef.current) {
      const c = centreRef.current || centre;
      m.easeTo({
        center: c,
        zoom: clampOpsMapZoom(OPS_MAP_DEFAULT_ZOOM_REGION),
        pitch: 0,
        bearing: 0,
        duration: prev === "globe" ? 980 : 0,
        essential: true,
        easing: (t) => 1 - Math.pow(1 - t, 3),
      });
      m.__softUserTs = Date.now();
    }
    return undefined;
  },[view,ready]);

  /* Keep free zoom when the site inventory lands — NO camera fly (was thrash). */
  useEffect(()=>{
    const m = map.current;
    if(!m || !ready || view !== "site") return;
    fenceSite(m, extent);
  },[extent,ready,view]);

  /* Side panel open/closed or docked bottom changes the free map plane. */
  useEffect(()=>{
    const m = map.current;
    const el = host.current;
    if(!m || !ready || !el) return;
    const id = requestAnimationFrame(() => {
      syncOpsMapViewport(m, el, { view });
      try {
        if (view === "site") fenceSite(m, extentRef.current);
      } catch {
        /* */
      }
    });
    const t = setTimeout(() => {
      syncOpsMapViewport(m, el, { view });
      try {
        if (view === "site") fenceSite(m, extentRef.current);
      } catch {
        /* */
      }
      /* Active volume: re-frame after padding settles (panel toggle/open zone). */
      try {
        const z = String(activeZoneId || "").trim();
        if (z && view === "site") {
          window.dispatchEvent(
            new CustomEvent("croc-zone-frame", { detail: { zoneId: z, reason: "panel" } }),
          );
        }
      } catch {
        /* */
      }
    }, 280);
    return () => { cancelAnimationFrame(id); clearTimeout(t); };
  },[sidePanelOpen, ready, view, activeZoneId]);

  /* Live plant pins — sticky pool · never remount whole fleet on poll (zoom sticky). */
  useEffect(() => {
    const m = map.current;
    if (!m || !ready) return;
    const apply = (opts = {}) => {
      const { hosts } = syncOpsLivePinPool(m, pool.current, {
        devices,
        people,
        centre,
        maplibregl,
        forceStructural: !!opts.force,
      });
      setNodes(hosts);
    };
    apply();
    wireOpsLivePinCamera(m, apply);
    /* AI MapOps Reality — Live SoT revision; Soft intent ghosts styled separately. */
    let cancelled = false;
    const syncAi = async () => {
      const open = (site.alarms || []).filter((a) => a && a.open);
      const hit = open.find((a) => a.id != null || a.alarm_id != null || a.event_id != null);
      const iid = hit
        ? String(
            hit.incident_id
            || hit.canonical_incident_id
            || hit.ai_incident_id
            || hit.id
            || hit.alarm_id
            || hit.event_id
            || "",
          )
        : "";
      if (!iid) {
        clearAiMapOpsLayers(m);
        setAiLiveSeats([]);
        setAiLoop(null);
        return;
      }
      const out = await fetchAndSyncAiMapOps(m, { incidentId: iid });
      if (cancelled) return;
      setAiLiveSeats(Array.isArray(out && out.liveSeats) ? out.liveSeats : []);
      setAiLoop((out && out.loop) || loopHudFromMapOps(null));
      if (out && out.ok) setAiMapOpsVisibility(m, true);
    };
    syncAi();
    const aiTimer = setInterval(syncAi, 8000);
    return () => {
      cancelled = true;
      clearInterval(aiTimer);
      /* Keep Markers alive across poll identity churn — only clear on map teardown. */
    };
  }, [ready, devices, people, centre, setNodes, site.alarms]);

  useEffect(() => {
    if (!ready) return undefined;
    return () => {
      clearOpsLivePinPool(pool.current);
      setNodes({});
    };
  }, [ready, setNodes]);

  /* Which way is up, where the marker is standing.
     Project the point, then project a point due north of it; the screen vector
     between the two is the local vertical. On a globe that vector swings as the
     sphere turns — near the lower limb it points down the screen — so a marker
     fed this angle leans and finally inverts, the way anything standing on a
     ball does when the ball rolls. Nothing in MapLibre computes this: its own
     "map" alignment is camera bearing and pitch only, identical for every
     marker regardless of where it sits. */
  useEffect(()=>{
    const m = map.current;
    if(!m || !ready || view !== "globe") return;
    const orient = ()=>{
      const mk = pool.current.__site; if(!mk) return;
      const ll = mk.getLngLat();
      const here  = m.project(ll);
      const north = m.project([ll.lng, Math.min(ll.lat + 0.75, 89.9)]);
      mk.setRotation(Math.atan2(north.x - here.x, here.y - north.y) * 180/Math.PI);
    };
    orient();
    m.on("move", orient);
    return ()=> m.off("move", orient);
  },[ready,view]);

  useEffect(()=>{
    const m = map.current; if(!m || !ready) return;
    const src = m.getSource("trail"); if(!src) return;
    const pts = layers.trail ? mapTrack : [];
    opsMapDeferSourceData(m, "trail", { type:"FeatureCollection", features: pts.length < 2 ? [] : [
      { type:"Feature", geometry:{ type:"LineString", coordinates:pts.map(p=>[p[0],p[1]]) } },
      ...pts.map(p=>({ type:"Feature", properties:{ at:p[2] },
        geometry:{ type:"Point", coordinates:[p[0],p[1]] } })) ] });
  },[layers.trail,mapTrack,ready]);

  useEffect(()=>{
    const m = map.current; if(!m || !ready) return;
    const v = k => layers[k] ? "visible" : "none";
    ["fence-fill","fence-line","fence-label","fence-alert","fence-alert-glow","fence-focus","fence-focus-glow"]
      .forEach(l=> m.getLayer(l) && m.setLayoutProperty(l,"visibility",v("fence")));
    m.getLayer("mesh-line") && m.setLayoutProperty("mesh-line","visibility",v("mesh"));
    const softVis = layers.softFleet ? "visible" : "none";
    ["soft-routes-glow","soft-routes-walk","soft-routes-bike","soft-routes-air","soft-assets-halo","soft-inc-ring"]
      .forEach((l) => m.getLayer(l) && m.setLayoutProperty(l, "visibility", softVis));
  },[layers,ready]);

  /* Soft multi-asset dispatch geometry (local Soft sim only). */
  useEffect(()=>{
    const m = map.current; if(!m || !ready) return;
    const empty = { type:"FeatureCollection", features:[] };
    const g = softGeo || {};
    const r = m.getSource("soft-routes");
    const a = m.getSource("soft-assets");
    const i = m.getSource("soft-incidents");
    if(r) opsMapDeferSourceData(m, "soft-routes", (layers.softFleet && g.routes) || empty);
    /* Never fill soft-assets or soft-incidents GL circles — Soft identity is DOM glyphs only. */
    if(a) opsMapDeferSourceData(m, "soft-assets", empty);
    if(i) opsMapDeferSourceData(m, "soft-incidents", empty);
    if(layers.softFleet) raiseSoftRouteLayers(m);
    syncSoftFleetMarkers(m, softPool.current, g, maplibregl, !!layers.softFleet);
  },[softGeo, layers.softFleet, ready]);

  /* Marching-beam dash on en-route legs (animated beam idea, native GL). */
  useEffect(()=>{
    const m = map.current; if(!m || !ready || !layers.softFleet) return;
    if(matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let step = 0, raf = 0, last = 0;
    const tick = (t)=>{
      raf = requestAnimationFrame(tick);
      if (opsMapCamYield(m)) return;
      if(t - last < 90) return;
      last = t; step = (step + 1) % 4;
      try{
        if(m.getLayer("soft-routes-walk"))
          m.setPaintProperty("soft-routes-walk","line-dasharray", SOFT_DASH_WALK[step]);
        if(m.getLayer("soft-routes-air"))
          m.setPaintProperty("soft-routes-air","line-dasharray", SOFT_DASH_AIR[step]);
        if(m.getLayer("soft-routes-bike"))
          m.setPaintProperty("soft-routes-bike","line-dasharray", SOFT_DASH_BIKE[step]);
      }catch{}
    };
    raf = requestAnimationFrame(tick);
    return ()=> cancelAnimationFrame(raf);
  },[ready, layers.softFleet]);

  /* cleanup soft markers with map */
  useEffect(()=>()=>{
    Object.values(softPool.current).forEach(x=>{ try{ x.remove(); }catch{} });
    softPool.current = {};
  },[]);

  /* Soft camera director: boot frame once · AI tour after 15 min human idle.
     softGeoRef avoids remount thrash on every sim tick · Soft≠Live. */
  const softBootFit = useRef(false);
  const softGeoRef = useRef(softGeo);
  softGeoRef.current = softGeo;
  const [softDir, setSoftDir] = useState(null);
  useEffect(()=>{
    const m = map.current;
    if(!m || !ready || view !== "site" || !layers.softFleet) return;
    const tick = () => {
      const g = softGeoRef.current;
      const feats = (g && g.pins && g.pins.features) || [];
      if(feats.length < 1) return;
      if(!softBootFit.current){
        softBootFit.current = true;
        m.__softNoAutoAfterBoot = true;
        softTryPriorityFrame(m, g, {
          force:true, maxZoom:16.1, duration:1100,
        });
      }
      const snap = tickSoftAiDirector(m, g, {});
      setSoftDir(snap);
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => clearInterval(id);
  },[layers.softFleet, ready, view]);

  /* Bubble Soft director snap for panel supervisor. */
  useEffect(()=>{
    if(typeof window === "undefined") return;
    window.__crocSoftDir = softDir;
  },[softDir]);

  /* Overview / Weave: Soft unit click → zoom that callsign (own roster only). */
  useEffect(()=>{
    if(typeof window === "undefined") return;
    const onFocus = (ev) => {
      const m = map.current;
      if(!m || !ready || !layers.softFleet) return;
      const id = ev && ev.detail && ev.detail.id != null ? String(ev.detail.id) : "";
      if(!id) return;
      const g = softGeoRef.current;
      const feats = (g && g.pins && g.pins.features) || [];
      const f = feats.find((x) => x && x.properties && String(x.properties.id) === id);
      const c = f && f.geometry && f.geometry.coordinates;
      if(!c || c.length < 2) return;
      m.__softOpenId = "a:" + id;
      if(typeof m.__softResync === "function"){
        try{ m.__softResync(); }catch{}
      }
      frameSoftFocus(m, [Number(c[0]), Number(c[1])], { zoom:16.65, duration:900 });
    };
    window.addEventListener("croc-soft-focus", onFocus);
    return () => window.removeEventListener("croc-soft-focus", onFocus);
  },[layers.softFleet, ready]);

  /* Zone enter — fit volume bounds · gentle · no thrash. Soft≠Live. */
  useEffect(()=>{
    if(typeof window === "undefined") return;
    const frameZone = (zid) => {
      const m = map.current;
      const el = host.current;
      if(!m || !ready || view !== "site") return;
      const id = String(zid || "").trim();
      if(!id) return;
      /* Re-measure panel/chrome first so setPadding matches free canvas. */
      try {
        if (el) syncOpsMapViewport(m, el, { view: "site" });
      } catch {
        /* */
      }
      const cat = softZoneCatalogOf({
        zones,
        devices,
        softState: isLocalHost() ? softFleetState : null,
        centre: site.centre,
      });
      const z = cat.find((x) => x.id === id || x.label === id);
      const ll = softZoneOriginOf(z, softFleetState) || (z && z.ll) || null;
      const bounds = softRingBoundsOf(z && z.ring);
      if (bounds) {
        frameSoftVolume(m, bounds, ll, {
          maxZoom: 17.2,
          duration: 880,
        });
        return;
      }
      if(!ll) return;
      frameSoftFocus(m, [Number(ll[0]), Number(ll[1])], {
        zoom: 16.2,
        maxZoom: 17.2,
        duration: 880,
      });
    };
    const onZoneFrame = (ev) => {
      const zid = ev && ev.detail && ev.detail.zoneId != null
        ? String(ev.detail.zoneId).trim()
        : String(activeZoneId || "").trim();
      if (!zid) return;
      /*
       * enterZone opens the side panel in the same tick — first paint still has
       * the old free plane. Frame now, then again after layout + padding measure
       * so the volume settles optically centered in the free map (not under panel).
       */
      frameZone(zid);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => frameZone(zid));
      });
      const t = window.setTimeout(() => frameZone(zid), 320);
      if (ev && ev.detail) {
        /* stash cancel handles on the last call via weak local cleanup below */
        onZoneFrame.__raf = raf;
        onZoneFrame.__t = t;
      }
    };
    window.addEventListener("croc-zone-frame", onZoneFrame);
    return () => {
      window.removeEventListener("croc-zone-frame", onZoneFrame);
      try {
        if (onZoneFrame.__raf) cancelAnimationFrame(onZoneFrame.__raf);
        if (onZoneFrame.__t) clearTimeout(onZoneFrame.__t);
      } catch {
        /* */
      }
    };
  },[ready, view, zones, devices, softFleetState, activeZoneId, site.centre, sidePanelOpen]);

  /*
   * Soft Skill Weave — map is the drop surface:
   * · dispatch: drag tray chip → map · skill route Soft
   * · place:    click/drop relocate Soft mirror only
   * · bearing:  click sets heading from unit toward drop
   * Soft≠Live: never writes Live ownership / map_lat.
   *
   * Drop precision: CSS-scale-aware client→map point + screen-space Soft pin
   * pick (≤56px) so the pin under the cursor wins over distant geo-nearest.
   */
  useEffect(()=>{
    if(typeof window === "undefined") return;
    const m = map.current;
    if(!m || !ready || !layers.softFleet || !softOps) return;

    const softBearingDeg = (lng0, lat0, lng1, lat1) => {
      const φ1 = (lat0 * Math.PI) / 180;
      const φ2 = (lat1 * Math.PI) / 180;
      const Δλ = ((lng1 - lng0) * Math.PI) / 180;
      const y = Math.sin(Δλ) * Math.cos(φ2);
      const x =
        Math.cos(φ1) * Math.sin(φ2) -
        Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
      return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
    };

    /** CSS px → MapLibre layout point (handles browser zoom / canvas CSS scale). */
    const clientToMapXY = (mapInst, clientX, clientY) => {
      if(!mapInst) return null;
      const el = (typeof mapInst.getCanvasContainer === "function"
        && mapInst.getCanvasContainer())
        || mapInst.getContainer();
      if(!el || !el.getBoundingClientRect) return null;
      const rect = el.getBoundingClientRect();
      if(!(rect.width > 0) || !(rect.height > 0)) return null;
      const layoutW = el.clientWidth || rect.width;
      const layoutH = el.clientHeight || rect.height;
      return {
        x: ((clientX - rect.left) * layoutW) / rect.width,
        y: ((clientY - rect.top) * layoutH) / rect.height,
      };
    };

    const clientToLngLat = (mapInst, clientX, clientY) => {
      const xy = clientToMapXY(mapInst, clientX, clientY);
      if(!xy) return null;
      try{
        const ll = mapInst.unproject([xy.x, xy.y]);
        if(!ll) return null;
        const lng = Number(ll.lng);
        const lat = Number(ll.lat);
        if(!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
        return { lng, lat, x: xy.x, y: xy.y };
      }catch{
        return null;
      }
    };

    /** Soft edge pin nearest to pointer in screen space (accurate at any zoom). */
    const pickSoftDevicePx = (mapInst, point, maxPx) => {
      if(!mapInst || !point) return null;
      const lim = Number.isFinite(maxPx) && maxPx > 0 ? maxPx : 56;
      const feats = (softGeoRef.current && softGeoRef.current.pins
        && softGeoRef.current.pins.features) || [];
      let best = null;
      let bestD = lim + 1e-9;
      for(const f of feats){
        const props = f && f.properties;
        const c = f && f.geometry && f.geometry.coordinates;
        if(!props || !c || c.length < 2) continue;
        if(String(props.kind || "") !== "device") continue;
        let p;
        try{ p = mapInst.project([Number(c[0]), Number(c[1])]); }
        catch{ continue; }
        if(!p) continue;
        const d = Math.hypot(Number(p.x) - point.x, Number(p.y) - point.y);
        if(d <= bestD){
          bestD = d;
          best = {
            id: String(props.id),
            label: String(props.label || props.id),
            ll: [Number(c[0]), Number(c[1])],
            px: d,
          };
        }
      }
      return best;
    };

    const applyAt = (lng, lat, preferDrag, pickMeta) => {
      const dragId =
        preferDrag && window.__crocSoftDragUnit
          ? String(window.__crocSoftDragUnit)
          : "";
      const selId = softOps.selectedId ? String(softOps.selectedId) : "";
      const id = dragId || selId;
      const mode = softOps.mode || window.__crocSoftInteractMode || "dispatch";
      if(!id){
        if(mode === "place" || mode === "bearing"){
          toast && toast("Pick a Soft unit on Skill Weave desk first", "warn");
        }
        return;
      }
      const ll = [Number(lng), Number(lat)];
      if(mode === "place"){
        softOps.placeUnit && softOps.placeUnit(id, ll);
        toast && toast("Soft place · " + id + " (mirror only)", "go");
      } else if(mode === "bearing"){
        let deg = 0;
        try{
          const feats = (softGeoRef.current && softGeoRef.current.pins &&
            softGeoRef.current.pins.features) || [];
          const f = feats.find((x) => x && x.properties && String(x.properties.id) === id);
          const c = f && f.geometry && f.geometry.coordinates;
          if(c && c.length >= 2){
            deg = softBearingDeg(Number(c[0]), Number(c[1]), ll[0], ll[1]);
          }
        }catch{ /* */ }
        softOps.setBearing && softOps.setBearing(id, deg);
        toast && toast("Soft bearing · " + id + " · " + Math.round(deg) + "°", "go");
      } else if(softOps.dispatchTo){
        const pin = pickMeta && pickMeta.device ? pickMeta.device : null;
        const goLl = pin && pin.ll ? pin.ll : ll;
        const opts = pin
          ? { preferDeviceId: pin.id, preferLl: pin.ll }
          : null;
        Promise.resolve(softOps.dispatchTo(id, goLl, opts))
          .then(() =>
            toast &&
              toast(
                pin
                  ? "Lab dispatch · " + id + " → " + (pin.label || pin.id) + " (session only)"
                  : "Lab dispatch · " + id + " enroute (session only)",
                "go",
              ),
          )
          .catch((e) =>
            toast && toast(String((e && e.message) || e || "Soft dispatch failed"), "warn"),
          );
      }
      window.__crocSoftDragUnit = "";
    };

    const onMapClick = (e) => {
      if(!e || !e.lngLat) return;
      const mode = softOps.mode || window.__crocSoftInteractMode || "dispatch";
      const hasUnit = !!(window.__crocSoftDragUnit || softOps.selectedId);
      if(mode === "dispatch" && !hasUnit) return;
      /* MapLibre click already has pixel-true lngLat + point. */
      let pick = null;
      if(e.point){
        pick = pickSoftDevicePx(map.current, e.point, 56);
      }
      applyAt(e.lngLat.lng, e.lngLat.lat, true, { device: pick });
    };
    const onMapDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const mapInst = map.current;
      if(!mapInst) return;
      const hit = clientToLngLat(mapInst, e.clientX, e.clientY);
      if(!hit) return;
      const pick = pickSoftDevicePx(mapInst, { x: hit.x, y: hit.y }, 56);
      applyAt(hit.lng, hit.lat, true, { device: pick });
    };
    const onDragOver = (e) => {
      e.preventDefault();
      try{ e.dataTransfer.dropEffect = "copy"; }catch{}
    };
    /* Capture on canvas container when present — same coord frame as project/unproject. */
    const canvasHost = (typeof m.getCanvasContainer === "function" && m.getCanvasContainer())
      || host.current;
    const el = canvasHost || host.current;
    m.on("click", onMapClick);
    if(el){
      el.addEventListener("dragover", onDragOver);
      el.addEventListener("drop", onMapDrop);
    }
    return () => {
      try{ m.off("click", onMapClick); }catch{}
      if(el){
        el.removeEventListener("dragover", onDragOver);
        el.removeEventListener("drop", onMapDrop);
      }
    };
  },[ready, layers.softFleet, softOps, toast]);

  /* The travelling boundary. MapLibre will not animate a dash offset, so the
     pattern is stepped instead — and it only runs while a zone is actually
     open, because a permanent animation is decoration and stops meaning
     anything the second time an operator sees it. */
  useEffect(()=>{
    const m = map.current; if(!m || !ready) return;
    /* Which boundary is live is a geometric question, not a spelling one. An
       alarm names a device; the server has already worked out which boundary
       that device physically stands in. Matching the alarm's free-text zone
       caption against the boundary's name used to light up whichever fence
       happened to share a word, and leave the right one dark. */
    const hot = new Set((site.alarms||[]).filter(a=>a.open).map(a=>a.dev).filter(Boolean));
    const open = zones.filter(z=>z.deviceIds.some(id=>hot.has(id))).map(z=>z.label);
    const crit = (site.alarms||[]).some(a=>a.crit);
    if(!m.getLayer("fence-alert")) return;
    m.setFilter("fence-alert", ["in","name",...open]);
    m.setFilter("fence-alert-glow", ["in","name",...open]);
    m.setPaintProperty("fence-alert","line-color", crit ? "#FF6B6B" : "#FFB65C");
    m.setPaintProperty("fence-alert-glow","fill-color", crit ? "#FF6B6B" : "#FFB65C");
    if(!open.length) return;
    if(matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let step = 0, raf = 0, last = 0;
    const tick = t=>{
      raf = requestAnimationFrame(tick);
      if (opsMapCamYield(m)) return;
      if(t - last < (crit ? 52 : 88)) return;
      last = t; step = (step + 1) % DASH.length;
      try{ m.setPaintProperty("fence-alert","line-dasharray", DASH[step]); }catch{}
    };
    raf = requestAnimationFrame(tick);
    return ()=> cancelAnimationFrame(raf);
  },[site.alarms,zones,ready,layers.fence]);

  /* Active volume ring marches — space focus feedback (not alarm). Soft≠Live. */
  useEffect(()=>{
    const m = map.current; if(!m || !ready) return;
    if (!m.getLayer("fence-focus")) return;
    const active = String(activeZoneId || "").trim();
    try {
      m.setFilter("fence-focus", ["==", ["get", "active"], 1]);
      m.setFilter("fence-focus-glow", ["==", ["get", "active"], 1]);
    } catch {
      /* */
    }
    if (!active) return;
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let step = 0, raf = 0, last = 0;
    const tick = (t) => {
      raf = requestAnimationFrame(tick);
      if (opsMapCamYield(m)) return;
      if (t - last < 72) return;
      last = t;
      step = (step + 1) % DASH.length;
      try {
        m.setPaintProperty("fence-focus", "line-dasharray", DASH[step]);
        const glow = 0.08 + (step % 4) * 0.025;
        m.setPaintProperty("fence-focus-glow", "fill-opacity", glow);
      } catch {
        /* */
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  },[ready, layers.fence, activeZoneId]);

  /* Intentional device fly only — never re-chase when the register refreshes. */
  const camFlyKey = useRef("");
  useEffect(()=>{
    const m = map.current; if(!m || !ready || view !== "site") return;
    const selId = deviceSelIdOf(selected) || "";
    const key = selId + "|" + flyKey + "|" + view;
    if(key === camFlyKey.current) return;
    camFlyKey.current = key;
    if(!selId) return;
    /* Soft user is dialled into a unit — do not steal the viewport. */
    if(m.__softOpenId || m.__softFocusHold) return;
    const d = selId && devices.find((x) => x.id === selId);
    if(d && d.ll){
      try{
        m.easeTo({
          center:d.ll,
          zoom:Math.max(m.getZoom(),15.9),
          duration:820,
          essential:true,
          easing:t=>1-Math.pow(1-t,4),
        });
      }catch{}
    }
  },[selected,ready,flyKey,view,devices]);

  const ctl = useCallback(fn=>{ const m = map.current; if(m) fn(m); },[]);
  return html`<${F}>
    ${/* The map is the page. It carries no visible title, so the title it
          announces to a screen reader is the only one it has. */""}
    <h1 className="a11y">Operations — ${view==="globe" ? "the world" : "site map"}</h1>
    <${AnimatePresence}>
      ${view==="globe" && html`
        <${M.div} key="sky" className="cosmos" initial=${{ opacity:0 }} animate=${{ opacity:1 }}
            exit=${{ opacity:0 }} transition=${{ duration:.9 }}>
          <div className="starfield"/>
          <div className="galaxy"/>
          <div className="neb neb--a"/><div className="neb neb--b"/>
          <div className="planet planet--rust"><i/></div>
          <div className="planet planet--ring"><i/><s/></div>
          <div className="planet planet--ice"><i/></div>
        <//>`}
    <//>
    <div className="map-host" ref=${host} data-view=${view}/>
    <div className="map-tint" data-view=${view}/><div className="map-veil" data-view=${view}/>
    ${view === "site" && aiLoop && aiLoop.incident_id
      ? html`<aside className="ops-loop-hud" data-soft-invent="0" data-cmc-signal-loop="1" aria-label="Signal loop">
          <header className="ops-loop-hud__head">
            <em>Signal loop</em>
            <b>${aiLoop.origin_playing || (Number(aiLoop.playing_n) > 0)
              ? (Number(aiLoop.live_n) > 1
                ? `${Number(aiLoop.playing_n) || 0} of ${Number(aiLoop.live_n)} live`
                : "Live on map")
              : "Pulling live"}</b>
          </header>
          <ol className="ops-loop-hud__spine">
            <li data-on=${aiLoop.origin_playing || Number(aiLoop.playing_n) > 0 ? "1" : (aiLoop.live_n ? "wait" : "0")}>Live</li>
            <li data-on=${aiLoop.snapshot ? "1" : "0"}>Snapshot</li>
            <li data-on=${aiLoop.record ? "1" : "0"}>Record</li>
            <li data-on=${aiLoop.close || aiLoop.close_ready ? "1" : (aiLoop.close_wait ? "wait" : "0")}>Close</li>
          </ol>
          ${Number(aiLoop.live_n) > 1 && Array.isArray(aiLoop.seats) && aiLoop.seats.length
            ? html`<ol className="ops-loop-hud__seats" data-cmc-live-seats="1" aria-label="Live cameras">
                ${aiLoop.seats.map((s, i) => html`
                  <li key=${(s && s.camera_id) || i}
                    data-on=${s && s.closed ? "1" : (s && s.playing ? "play" : "wait")}
                    data-origin=${s && s.origin ? "1" : "0"}
                    data-nearby=${s && s.nearby ? "1" : "0"}>
                    ${s && s.nearby ? "Nearby" : (s && s.origin ? "At the point" : "Live")}
                  </li>`)}
              </ol>`
            : null}
          ${Number(aiLoop.live_n) > 1
            ? html`<p className="ops-loop-hud__line">${Number(aiLoop.playing_n) || 0} of ${Number(aiLoop.live_n)} live cameras · each keeps its own proof</p>`
            : aiLoop.nearby_n
            ? html`<p className="ops-loop-hud__line">${aiLoop.nearby_n} nearby seat${aiLoop.nearby_n === 1 ? "" : "s"}</p>`
            : html`<p className="ops-loop-hud__line">One camera at the point</p>`}
          ${aiLoop.close_wait && !aiLoop.close_ready
            ? html`<p className="ops-loop-hud__wait">${closeWaitCopy(aiLoop.close_wait)}</p>`
            : null}
        </aside>`
      : null}
    ${view === "site" && (aiHud.signals || aiHud.routes)
      ? html`<div className="ops-cortex-hud" data-soft-ne-live="1" data-stage=${aiHud.stage || "idle"}
          title="Cortex Resolve · simulated observe · not real actuation">
          <header className="ops-cortex-hud__head">
            <em className="ops-cortex-hud__brand">Cortex</em>
            <b className="ops-cortex-hud__tag">sim</b>
          </header>
          <ol className="ops-cortex-hud__stages" aria-label="Resolve stages">
            ${(aiHud.stages || ["observe","score","route","ready"]).map((s) => html`
              <li key=${s} data-on=${aiHud.stage === s || (aiHud.stage === "resolve" && (s === "route" || s === "ready")) ? "1" : "0"}>${s}</li>
            `)}
          </ol>
          <p className="ops-cortex-hud__line">${aiHud.line}</p>
          ${aiHud.topLabel
            ? html`<p className="ops-cortex-hud__top">${aiHud.topLabel}</p>`
            : null}
        </div>`
      : null}
    <${OpsMapControlsDesk} ctl=${ctl} onPanel=${onPanel} panelOpen=${panelOpen}
      onFocus=${onFocus} focusOpen=${focusOpen}
      view=${view} setView=${setView} centre=${centre} devices=${devices}
      toast=${toast} seat=${seat}/>
  <//>`;
}

function Markers({ nodes, selected, onSelect, layers, view, setView, person, onPerson, volumeFocus, mediaLive, peopleLive }){
  const s = useSite();
  const people = peopleLive || s.people || [];
  const globe = view === "globe";
  const selId = deviceSelIdOf(selected);
  const liveLamp = mediaLive && typeof mediaLive === "object" ? mediaLive : {};
  /* Severity, not a boolean. A node that is merely open and a node that is
     critical should not share a rhythm — the difference has to survive being
     seen out of the corner of an eye. */
  const alarmLvl = id =>{
    const a = s.alarms.find(x=>x.dev===id);
    return !a ? "0" : a.crit ? "2" : "1";
  };
  const volOut = (ll) => {
    if (!volumeFocus || !volumeFocus.origin || !ll) return false;
    return !softLngLatInRadius(ll, volumeFocus.origin, volumeFocus.radiusM);
  };
  return html`<${F}>
    ${nodes.__site && createPortal(html`
      <${AnimatePresence}>
        ${globe && html`
          <${M.button} key="beacon" className="beacon" initial=${{ opacity:0, scale:.4 }}
              animate=${{ opacity:1, scale:1 }} exit=${{ opacity:0, scale:.5 }}
              transition=${{ ...SOFT, delay:.9 }} onClick=${e=>{ stop(e); setView("site"); }}>
            <i className="beacon__ping"/><i className="beacon__ping" style=${{ animationDelay:"1.1s" }}/>
            <i className="beacon__shaft"/>
            <span className="beacon__head"><b/></span>
            <span className="beacon__lbl">Your site<em>${s.devices.length} boards · enter</em></span>
          <//>`}
      <//>`, nodes.__site, "site")}
    ${s.devices.map(d=>{
      const el = nodes[d.id]; if(!el) return null;
      const off = s.offline.includes(d.id);
      if(globe || (off && !layers.offline)) return createPortal(null, el, d.id);
      /* Live edge plant — multi-function CORE mark; cam is accessory only. */
      const meta = opsHwPinMetaOf(d);
      const out = volOut(d.ll);
      return createPortal(html`
        <div className=${meta.className} data-on=${selId===d.id?"1":"0"} data-alarm=${alarmLvl(d.id)}
             data-off=${off?"1":"0"} data-hw="1" data-fixed="1"
             data-cam=${meta.hasCam?"1":"0"}
             data-vol=${out?"0":"1"}
             data-facets=${meta.facets.join(" ")}
             data-kind=${String(d.kind||"node")}
             title=${meta.title}
             onClick=${e=>{ stop(e); onSelect(d.id); }}>
          <${OpsHwMark} device=${d} size=${34}/>
          <span className="pin__tag pin__tag--hw">
            <u>Core</u>
            <b>${selId===d.id ? "Inspect" : d.label}</b>
            <em>${meta.strip}</em>
          </span>
        </div>`, el, d.id);
    })}
    ${people.map(p=>{
      const el = nodes["p:"+p.id]; if(!el) return null;
      if(globe || !layers.people || !p.ll) return createPortal(null, el, "p:"+p.id);
      /* Accuracy is drawn to scale when it is known. An unknown radius is not
         drawn at all rather than defaulted, because a small circle claims a
         precision nobody reported. */
      const acc = Number.isFinite(p.acc) ? Math.min(Math.max(p.acc,4),90)*2.6 : 0;
      const liveMedia = liveLamp[p.id] === true;
      return createPortal(html`
        <div className="ppin" data-on=${person===p.id?"1":"0"} data-state=${p.state}
             data-role=${p.role} data-stale=${p.stale?"1":"0"}
             data-live-media=${liveMedia?"1":"0"}
             onClick=${e=>{ stop(e); onPerson(person===p.id?null:p.id); }}>
          ${acc>0 && html`<span className="ppin__acc" style=${{ width:acc+"px", height:acc+"px" }}/>`}
          <span className="ppin__av">
            <b>${initialsOf(p.id)}</b>
            <i className="ppin__role" title=${p.role}><${Ico} d=${ROLE_GLYPH[p.role]||"user"} w=${9}/></i>
            ${p.onCall === true && html`<s className="ppin__oncall"/>`}
            ${liveMedia && html`<s className="ppin__live" title="Authoritative live media"/>`}
          </span>
          <span className="ppin__tag">
            <b>${p.name || p.id}</b>
            <em>${p.name && p.name !== p.id ? p.id + " · " : ""}${p.callsign ? p.callsign + " · " : ""}${p.phone || "no number on file"}</em>
          </span>
        </div>`, el, "p:"+p.id);
    })}
  <//>`;
}

/* ═══════════════════ panel bodies ═══════════════════════════════════════ */
/* Soft≠Live Page64 — Overview Live desk in ops-overview-desk.js (HITL Page41 retained). */

function Devices({ selected, onSelect, toast }){
  return html`<${OpsDevicesDesk} selected=${selected} onSelect=${onSelect} toast=${toast}/>`;
}

/* Soft≠Live Page35: Ops device detail hydrates GET /devices/{id}.
   Soft null→string invent (rssi+" dBm" / vbat.toFixed) stays deleted. */
/* Soft≠Live Page226: map-selected hardware → dual survey∥act inspect desk.
   Soft free-roam invent for Live pins stays deleted. */
function DeviceDetail({ d, onBack, onLive, toast }){
  const s = useSite();
  const offList = s.offline.includes(d.id);
  const alarm = s.alarms.find((a) => a.dev === d.id) || null;
  return html`<${OpsDeviceInspectDesk}
    d=${d}
    offline=${offList}
    alarm=${alarm}
    onBack=${onBack}
    onLive=${onLive}
    toast=${toast}
  />`;
}

function Alarms({ onSelect, toast }) {
  return html`<${OpsAlarmsDesk} onSelect=${onSelect} toast=${toast}/>`;
}

/* Soft≠Live: Ops panel never remounts a second Cameras city — jump to primary. */
function Cameras({ toast }) {
  useEffect(() => {
    try { go("/cameras/live"); } catch { /* */ }
    toast && toast("Opening Cameras Live", "go");
  }, [toast]);
  return html`<div className="ops-hud" data-cmc-cameras-handoff="primary">
    <${Alert} tone="info" title="Cameras Live is the singular wall"
      say="Capture & bind stays under Cameras → Manage. This Ops seat no longer hosts a second list."/>
  </div>`;
}

/* Soft≠Live Page180 — full dispatchable desk (own roster · posture SoT · Soft≠Live). */
function Dispatch({ onSelect, onPerson, toast, fleet }) {
  return html`<${OpsDispatchDesk}
    onSelect=${onSelect}
    onPerson=${onPerson}
    toast=${toast}
    fleet=${fleet}/>`;
}

/* Soft Command OS — operational workbench (map remains spatial). Soft≠Live. */
function Workbench({ toast, fleet, activeZoneId }) {
  const s = useSite();
  return html`<${OpsCommandWorkbench}
    toast=${toast}
    fleet=${fleet}
    activeZoneId=${activeZoneId}
    liveZones=${s.zones}
    devices=${s.devices}/>`;
}

function Zones({ toast, fleet, activeZoneId, onEnterZone, onClearZone }) {
  const s = useSite();
  return html`<${OpsZonesDesk}
    toast=${toast}
    fleet=${fleet}
    activeZoneId=${activeZoneId}
    onEnterZone=${onEnterZone}
    onClearZone=${onClearZone}
    liveZones=${s.zones}
    devices=${s.devices}/>`;
}

function Resources({ toast }) {
  return html`<${OpsResourceOntologyDesk} toast=${toast}/>`;
}

/* ═══════════════════ panel shell — Ops rail IA v350 ═══════════════════ */
const PANEL_TABS = opsPanelTabLabelsOf();

function Panel({ tab, selected, onSelect, onPerson, onShot, onLive, toast, fleet, open, onToggle,
  activeZoneId, onEnterZone, onClearZone, seat, onFocusDevice, onResolved }){
  const s = useSite();
  const selId = deviceSelIdOf(selected);
  /* Only scoped register rows or an explicit Plant result may open Live inspect. */
  const d = selId
    ? (s.devices.find((x) => x.id === selId)
      || (selected && typeof selected === "object" && String(selected.id || selected.device_id || "") === selId
        ? {
            id: selId,
            label: String(selected.label || selected.display_label || selId),
            kind: String(selected.kind || "device"),
            zone: String(selected.zone || ""),
            group: String(selected.group || ""),
            age: Number.isFinite(Number(selected.age)) ? Number(selected.age) : -1,
            offline: !!selected.offline,
            surveyed: !!selected.surveyed,
            ll: Array.isArray(selected.ll) ? selected.ll : null,
          }
        : null))
    : null;
  const item = opsPanelItemOf(tab);
  const title = d
    ? d.label
    : activeZoneId && tab === "zones"
      ? String(activeZoneId)
      : (item && item.label) || PANEL_TABS[tab] || "AI";
  const fill = !d && opsPanelFillTab(tab);
  /* v410 shell owns title strip · every desk is stage-fill content */
  const alarmN = (s.alarms && s.alarms.length) || 0;
  const scrollRef = useRef(null);
  useEffect(() => {
    try {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    } catch {
      /* */
    }
  }, [tab, selId, activeZoneId]);
  /* Map wheel must not steal while pointer is over the Ops side archive. */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof el.addEventListener !== "function") return undefined;
    const stopMapSteal = (ev) => {
      try {
        ev.stopPropagation();
      } catch {
        /* */
      }
    };
    el.addEventListener("wheel", stopMapSteal, { passive: true });
    el.addEventListener("touchmove", stopMapSteal, { passive: true });
    return () => {
      try {
        el.removeEventListener("wheel", stopMapSteal);
        el.removeEventListener("touchmove", stopMapSteal);
      } catch {
        /* */
      }
    };
  }, [tab, open, selId]);

  if (!open) {
    return html`
      <${M.div}
        initial=${{ x: 22, opacity: 0, scale: 0.96 }}
        animate=${{ x: 0, opacity: 1, scale: 1 }}
        exit=${{ x: 14, opacity: 0, scale: 0.97 }}
        transition=${SPRING}>
        <${OpsPanelReveal}
          tab=${tab}
          alarmN=${alarmN}
          onOpen=${() => onToggle && onToggle(true)}/>
      <//>`;
  }

  return html`
    <${M.aside} className="panel glass panel--ops panel--ops-rail"
        data-fill=${fill ? "1" : "0"}
        data-cmc-panel="ops-side"
        data-ops-ia="ops-ai-v620"
        data-zone=${activeZoneId || ""}
        data-local=${isLocalHost() ? "1" : "0"}
        data-theatre="1"
        data-kinetic="1"
        data-warm="1"
        data-arena="1"
        initial=${{ x: 12, opacity: 0 }}
        animate=${{ x: 0, opacity: 1 }}
        exit=${{ x: 8, opacity: 0 }}
        transition=${{ duration: 0.16, ease: [0.2, 0, 0.2, 1] }}>
      ${d
        ? html`<div className="panel__top ops-rail__dev-top">
            <div className="panel__hd">
              <p className="panel__k">Device</p>
              <h2 title=${title}>${title}</h2>
              <em className="panel__id">${d.id}</em>
            </div>
            <button type="button" className="icon-btn panel__hide" title="Hide side panel"
              aria-label="Hide side panel"
              onClick=${() => onToggle && onToggle(false)}>
              <${Ico} d="arrowR" w=${14}/>
            </button>
          </div>
          <div ref=${scrollRef} className="panel__scroll">
            <${AnimatePresence} mode="wait">
              <${M.div} key=${"dev-"+d.id}
                  className="panel__body"
                  initial=${{ opacity:0, y:8 }}
                  animate=${{ opacity:1, y:0 }}
                  exit=${{ opacity:0, y:-6 }}
                  transition=${{ type:"spring", stiffness:420, damping:36 }}>
                <${DeviceDetail} d=${d} onBack=${()=>onSelect(null)} onLive=${onLive} toast=${toast}/>
              <//>
            <//>
          </div>`
        : html`<div
            className="ops-rail-layout ops-shell-layout ops-shell-layout--cockpit"
            data-fill=${fill ? "1" : "0"}
            data-ops-shell="v620"
            data-face=${opsPanelFaceOf(tab)}>
            ${opsPanelFaceOf(tab) === "ai"
              ? html`<div ref=${scrollRef} className="ops-cockpit-stage">
                  <${OpsAiWorkspace}
                    toast=${toast}
                    fleet=${fleet}
                    seat=${seat}
                    onFocusDevice=${onFocusDevice}
                    onResolved=${onResolved}
                    onHide=${() => onToggle && onToggle(false)}
                    onOpenObserve=${() => {
                      try { go("/ops/devices"); } catch { /* */ }
                    }}/>
                </div>`
              : html`<${F}>
                  <${OpsObserveChrome}
                    tab=${tab}
                    alarmN=${alarmN}
                    activeZoneId=${activeZoneId}
                    onHide=${() => onToggle && onToggle(false)}/>
                  <div className="ops-rail-stage ops-shell-stage ops-shell-stage--obs" data-tab=${tab}>
                    <div ref=${scrollRef} className=${"ops-rail-stage__body ops-shell-stage__body" + (fill ? " ops-rail-stage__body--fill ops-shell-stage__body--fill" : "")}>
                      <${AnimatePresence} mode="wait" initial=${false}>
                        <${M.div} key=${tab}
                            className=${"ops-shell-stage__pane " + (fill ? "panel__fill" : "panel__body")}
                            data-theatre-pane="1"
                            initial=${{ opacity: 0 }}
                            animate=${{ opacity: 1 }}
                            exit=${{ opacity: 0 }}
                            transition=${{ duration: 0.12, ease: [0.2, 0, 0.2, 1] }}>
                          ${tab==="overview" ? html`<${OpsOverviewDesk}
                                onSelect=${onSelect} onShot=${onShot} toast=${toast}
                                selected=${selected} fleet=${fleet}
                                activeZoneId=${activeZoneId}
                                onEnterZone=${onEnterZone} onClearZone=${onClearZone}/>`
                            : tab==="workbench" ? html`<${Workbench} toast=${toast} fleet=${fleet} activeZoneId=${activeZoneId}/>`
                            : tab==="zones" ? html`<${Zones} toast=${toast} fleet=${fleet}
                                activeZoneId=${activeZoneId} onEnterZone=${onEnterZone} onClearZone=${onClearZone}/>`
                            : tab==="dispatch" ? html`<${Dispatch} onSelect=${onSelect} onPerson=${onPerson} toast=${toast} fleet=${fleet}/>`
                            : tab==="devices" ? html`<${Devices} selected=${selected} onSelect=${onSelect} toast=${toast}/>`
                            : tab==="alarms"  ? html`<${Alarms} onSelect=${onSelect} toast=${toast}/>`
                            : tab==="cameras" ? html`<${Cameras} onShot=${onShot} toast=${toast}/>`
                            : tab==="resources" ? html`<${Resources} toast=${toast}/>`
                            : null}
                        <//>
                      <//>
                    </div>
                  </div>
                <//>`}
          </div>`}
    <//>`;
}

/* ═══════════════════ decision bar ═══════════════════════════════════════ */
/* Soft≠Live Page96 + Page216: bare Approve/Hold → CTAs; Live POST only; fail-closed seat. */
function Decision({ gate, onResolve, seat }){
  const [wait,setWait] = useState(0);
  const hitlOk = !!(seat && (seat.hitlAllowed || seat.decisionAllowed));
  useEffect(()=>{
    if(!gate) return; setWait(gate.wait);
    const id = setInterval(()=>setWait(w=>w>0?w-1:0),1000);
    return ()=>clearInterval(id);
  },[gate]);
  if(!gate) return null;
  const mm = String(Math.floor(wait/60)).padStart(2,"0"), ss = String(wait%60).padStart(2,"0");
  return html`
    <${M.div} className="decision glass decision--liquid"
        data-cmc-page="page96-ops-decision" data-cmc-residual="page216-ops-map" data-cmc-shell="ops-ux"
        initial=${{ y:26, opacity:0 }} animate=${{ y:0, opacity:1 }}
        exit=${{ y:18, opacity:0 }} transition=${SPRING}>
      <div className="body">
        <div className="meta">
          <span className="seal"><i/> waiting on you</span>
          <span className="id">${gate.title ? "Open plan" : "Decision"}</span>
          <span className="id">held ${mm}:${ss}</span>
        </div>
        <h2>${gate.title}</h2>
        <p>${gate.body}</p>
        <p className="decision__note">
          ${!hitlOk ? "Only a manager can continue or hold this plan." : "Continue or Hold — you stay on the loop. Not approve-every-step."}
        </p>
      </div>
      <div className="acts decision__acts">
        <button type="button" className="btn btn--ghost" disabled=${!hitlOk}
          onClick=${()=>{
            if (!hitlOk) return;
            onResolve("hold");
          }}>Hold</button>
        <button type="button" className="btn btn--go" disabled=${!hitlOk}
          onClick=${()=>{
            if (!hitlOk) return;
            onResolve("approve");
          }}>Continue</button>
      </div>
    <//>`;
}

/* ═══════════════════ lightbox ═══════════════════════════════════════════ */
function Lightbox({ shot, onClose }) {
  const [src, setSrc] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    let dead = false;
    let obj = "";
    setSrc("");
    setErr("");
    if (!shot || !shot.id || shot.gone) {
      setErr(shot && shot.gone ? "expired" : "missing");
      return undefined;
    }
    (async () => {
      try {
        const r = await api(
          path("get__admin_camera-snapshots_p_image", { pathParams: [shot.id] }),
          { raw: true },
        );
        if (r.status === 410) {
          if (!dead) setErr("expired");
          return;
        }
        const blob = await r.blob();
        if (!blob || !blob.size) {
          if (!dead) setErr("empty");
          return;
        }
        obj = URL.createObjectURL(blob);
        if (!dead) setSrc(obj);
      } catch (e) {
        if (!dead) setErr((e && e.message) || "load failed");
      }
    })();
    return () => {
      dead = true;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [shot && shot.id]);
  return html`
    <${M.div} className="scrim" initial=${{ opacity:0 }} animate=${{ opacity:1 }} exit=${{ opacity:0 }}
        transition=${FADE} onClick=${onClose}>
      <${M.div} className="light glass" initial=${{ scale:.95, y:12 }} animate=${{ scale:1, y:0 }}
          exit=${{ scale:.97, y:8 }} transition=${SOFT} onClick=${stop}>
        <div className="light__in">
          <div className="light__hd">
            <b>${shot.cam}</b><em>${shot.at} · ${shot.id}</em>
            <button className="icon-btn" style=${{ marginLeft:"auto" }} onClick=${onClose}><${Ico} d="close" w=${14}/></button>
          </div>
          <div className="light__img">
            ${src
              ? html`<img src=${src} alt=${"frame " + shot.id} style=${{ maxWidth:"100%", maxHeight:"70vh", display:"block" }}/>`
              : html`<span>${err === "expired" ? "This frame left retention." : (err || "Loading frame…")}</span>`}
          </div>
          <div className="btn-row" style=${{ marginTop:14 }}>
            <span className="chip" data-tone="off">${shot.ttl}</span>
            <span className="chip" data-tone="off">Secure frame</span>
          </div>
        </div>
      <//>
    <//>`;
}

/* ═══════════════════ μDock live session ════════════════════════════════
   Soft≠Live Page42: real TTL grant + opaque remux pull. Zero Soft players. */
function Live({ dev, onClose, toast }){
  const udockPosture = useApi(path("get__udock_status"));
  const liveOk = udockPosture.loading ? false : (
    !!(udockPosture.data && udockPosture.data.write_allowed === true)
  );
  const [sess, setSess] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const reason = dev.alarmId
    ? ("Open live on alarm · ops map")
    : "Open live video · ops map";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (udockPosture.loading) return;
      if (!liveOk) {
        const sealed = (udockPosture.data && udockPosture.data.armed === true)
          ? "Your account cannot open live video on this unit."
          : "Live video is not armed on this site yet.";
        if (cancelled) return;
        setErr(sealed);
        toast && toast(sealed, "warn");
        return;
      }
      setBusy(true);
      let cameraId = "";
      try {
        const pack = await api(path("get__admin_devices_p_camera-bindings", { pathParams: [dev.id] }));
        const rows = (pack && pack.items) || (pack && pack.bindings) || pack || [];
        const first = Array.isArray(rows) ? rows[0] : null;
        cameraId = (first && (first.camera_id || first.cameraId)) || "";
      } catch (_e) { /* missing bindings → open without Digest */ }
      const out = await openLiveSession(dev.id, {
        operator_reason: reason,
        ttl_s: E.UDOCK.ttl,
        alarm_id: dev.alarmId || null,
        trigger_kind: dev.triggerKind || "",
        camera_id: cameraId,
        camera_hint: cameraId ? "" : (dev.cameraHint || ""),
      });
      if (cancelled) return;
      setBusy(false);
      if (!out.ok) { setErr(out.error); toast(out.error, "bad"); return; }
      const item = (out.data && out.data.item) || out.data;
      setSess(item);
      const played = item && item.media_played;
      toast(
        played
          ? "Live open · media is playing"
          : "Session started · waiting for the unit to stream",
        "go",
      );
    })();
    return () => { cancelled = true; };
  }, [dev.id, dev.alarmId, reason, toast, dev.triggerKind, dev.cameraHint, liveOk, udockPosture.loading]);

  useEffect(() => {
    if (!sess || !sess.session_id) return undefined;
    if (["closed", "expired"].includes(sess.state)) return undefined;
    const t = setInterval(async () => {
      const out = await getLiveSession(dev.id, sess.session_id);
      if (out.ok) {
        const item = (out.data && out.data.item) || out.data;
        setSess(item);
      }
    }, 2000);
    return () => clearInterval(t);
  }, [dev.id, sess && sess.session_id, sess && sess.state]);

  const renew = async () => {
    if (!liveOk) {
      toast && toast(
        (udockPosture.data && udockPosture.data.armed === true)
          ? "Your account cannot open live video on this unit."
          : "Live video is not armed on this site yet.",
        "warn",
      );
      return;
    }
    if (!sess) return;
    setBusy(true);
    const out = await renewLiveSession(dev.id, sess.session_id, "Renew live video · ops map");
    setBusy(false);
    if (!out.ok) { toast(out.error, "bad"); return; }
    setSess((out.data && out.data.item) || out.data);
    toast("Session renewed", "go");
  };

  const close = async () => {
    if (sess && sess.session_id) {
      setBusy(true);
      await closeLiveSession(dev.id, sess.session_id, "Close live video · ops map");
      setBusy(false);
    }
    onClose();
  };

  const mm = sess ? String(Math.floor((sess.seconds_remaining || 0) / 60)).padStart(2, "0") : "--";
  const ss = sess ? String((sess.seconds_remaining || 0) % 60).padStart(2, "0") : "--";
  const hasBytes = !!(sess && (sess.viewer_url || sess.egress_endpoint));
  const mint = (sess && sess.mint_result) || null;
  const stateLabel = sess
    ? (sess.state === "open" || sess.state === "active" ? "open"
      : sess.state === "closed" ? "closed"
      : sess.state === "expired" ? "expired"
      : String(sess.state || "working"))
    : (busy ? "opening…" : "—");

  /* Mini surface: signal-opened window docks over the map; no scrim, map stays usable. */
  const mini = !!(dev && dev.mini) && !expanded;
  return html`
    <${M.div} className=${mini ? "scrim scrim--live-mini" : "scrim"} data-cmc-live="page42" data-surface=${mini ? "mini" : "desk"}
        data-cmc-ops-live-ok=${liveOk ? "1" : "0"} initial=${{ opacity:0 }} animate=${{ opacity:1 }} exit=${{ opacity:0 }}
        transition=${FADE} onClick=${mini ? stop : close}>
      <${M.div} className=${mini ? "light glass light--udock light--udock-mini" : "light glass light--udock"}
          initial=${{ scale:.95, y:12 }} animate=${{ scale:1, y:0 }}
          exit=${{ scale:.97, y:8 }} transition=${SOFT} onClick=${stop}>
        <div className="light__in">
          <div className="light__hd">
            <span className="livedot"/><b>${dev.label}</b>
            <em>Live video · ${stateLabel}</em>
            <span className="ttl">${mm}:${ss}</span>
            ${dev && dev.mini ? html`<button className="icon-btn" title=${mini ? "Expand" : "Shrink"}
              aria-label=${mini ? "Expand live window" : "Shrink live window"}
              onClick=${() => setExpanded((v) => !v)}><${Ico} d=${mini ? "plus" : "minus"} w=${14}/></button>` : null}
            <button className="icon-btn" onClick=${close}><${Ico} d="close" w=${14}/></button>
          </div>
          ${err && html`<${Alert} tone="bad" title="Could not open live video" say=${err}/>`}
          ${sess && html`<${Alert} tone=${hasBytes ? "info" : "warn"}
            title=${hasBytes ? "Stream ready" : "Waiting for the unit"}
            say=${sess.honesty
              ? String(sess.honesty).replace(/\/[a-z0-9_\-{}]+/gi, "…").slice(0, 160)
              : "The console never invents a fake player when the unit is silent."}/>`}
          <div className="light__img live__img">
            ${hasBytes && sess.viewer_url
              ? html`<a href=${sess.viewer_url} target="_blank" rel="noreferrer">Open secure viewer</a>`
              : html`<span>${sess
                ? (sess.media_played
                  ? "Media is reaching the console"
                  : "Session ready · waiting for camera stream")
                : "Opening live video…"}</span>`}
          </div>
          <${KV} k="Renewals" v=${sess ? (sess.renewals + " / " + sess.max_renewals) : "—"}/>
          <${KV} k="Camera login" v=${sess ? (sess.has_digest ? "ready" : "not set") : "—"}
            tone=${sess && sess.has_digest ? "hi" : "warn"}/>
          <${KV} k="Stream path" v=${hasBytes ? "reported" : "waiting for unit"}
            tone=${hasBytes ? "hi" : "dim"}/>
          <${KV} k="Playing" v=${sess
            ? (sess.media_played ? "yes" : "not yet")
            : "—"}
            tone=${sess && sess.media_played ? "hi" : "warn"}/>
          ${mint ? html`<${KV} k="Link status"
            v=${String(mint.state || mint.backend || mint.reason || "checking")}/>` : null}
          ${sess && sess.session_id ? html`
            <${GrantLatticePlayer}
              deviceId=${dev.id}
              sessionId=${sess.session_id}
              grant=${sess.grant_lattice}
              mediaPlayed=${sess.media_played === true}
              surface="desk"
              toast=${toast}/>
            <${RemuxPullDesk} deviceId=${dev.id} sessionId=${sess.session_id} toast=${toast} auto=${true}/>` : null}
          <div className="btn-row" style=${{ marginTop:14 }}>
            <button className="btn btn--ghost btn--sm" disabled=${busy || !sess || !liveOk} onClick=${renew}>
              Renew</button>
            <button className="btn btn--danger btn--sm" disabled=${busy} onClick=${close}>Close</button>
          </div>
          <p className="note">Live grants expire · only units you can see open · no invented players.</p>
        </div>
      <//>
    <//>`;
}

/* ═══════════════════ a person on the map ══════════════════════════════ */
/* Soft≠Live Page67 — track + person desk in ops-person-sheet-desk.js */
function useTrack(username, active) {
  return usePersonnelTrack(username, active);
}

/* ═══════════════════ exports ═══════════════════════════════════════════ */
export function OpsMap({ panelTab, toast }){
  const [sel,setSel] = useState(null);
  const [nodes,setNodes] = useState({});
  const { seat, posture } = useOpsMapSeat();
  const [layers,setLayers] = useState({
    fence:true, mesh:true, offline:true, people:true, trail:false,
    softFleet: false,
  });
  // Soft≠Live Page216: Soft fleet never on production; never auto-paint Soft on map
  // (operator opts in via Layers on 127 Soft≠ invent Soft theatre by default).
  useEffect(() => {
    const softOk = isLocalHost() && !!(seat && seat.softFleetLocalOnly) && !!(seat && seat.mapAllowed);
    setLayers((x) => {
      if (!softOk && x.softFleet) return { ...x, softFleet: false };
      return x;
    });
  }, [seat.mapAllowed, seat.softFleetLocalOnly]);
  const [layersOpen,setLayersOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [shot,setShot] = useState(null);
  const [camLive,setCamLive] = useState(null);
  const [view,setView] = useState("site");
  const [person,setPerson] = useState(null);
  const [personTab,setPersonTab] = useState("who");
  const openPerson = (id, tab) => {
    setPersonTab(id && tab === "assign" ? "assign" : "who");
    setPerson(id);
  };
  const [res,setRes] = useState({});
  const [fly,setFly] = useState(0);
  const [activeZoneId, setActiveZoneId] = useState(() => loadActiveZoneId());
  /* Region filter (multi-select) + auto-live on signal — user prefs, Live geometry only.
     Unset auto-live follows Policy Kernel: AUTO opens the window, explicit 0/1 wins. */
  const { me } = useSession();
  const focusUser = String((me && me.username) || "").trim();
  const [focus, setFocus] = useState(emptyFocus);
  const [focusOpen, setFocusOpen] = useState(false);
  const focusSeen = useRef("");
  useEffect(() => {
    if (!focusUser || focusSeen.current === focusUser) return;
    focusSeen.current = focusUser;
    const saved = loadMapFocus(focusUser);
    setFocus(saved);
    if (!saved.places.length) setFocusOpen(true);
  }, [focusUser]);
  const commitFocus = useCallback((next) => {
    setFocus(saveMapFocus(focusUser, next));
  }, [focusUser]);
  const boardOn = !!(focus.exclusive && focus.places.length);
  const [miss, setMiss] = useState("");
  const pageOnly = boardOn || !!miss;
  const viewFocus = boardOn ? focus : emptyFocus();
  const focusRef = useRef(focus);
  focusRef.current = focus;
  const leaveBoard = useCallback(() => {
    setMiss("");
    setFocusOpen(false);
    const cur = focusRef.current;
    if (cur && cur.exclusive) commitFocus(leaveMapFocus(cur));
  }, [commitFocus]);
  useEffect(() => {
    if (boardOn) setView("site");
  }, [boardOn]);
  useEffect(() => {
    const root = document.documentElement;
    if (miss) root.dataset.cmcFocusBoard = "miss";
    else if (boardOn) root.dataset.cmcFocusBoard = "board";
    else delete root.dataset.cmcFocusBoard;
    return () => { delete root.dataset.cmcFocusBoard; };
  }, [miss, boardOn]);
  useEffect(() => {
    if (!pageOnly) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      leaveBoard();
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [pageOnly, leaveBoard]);
  const [regionIds, setRegionIds] = useState(() => loadRegionIds());
  const [siteIds, setSiteIds] = useState(() => loadSiteIds());
  const [autoLivePref, setAutoLivePrefState] = useState(() => loadAutoLivePref());
  const custody = useApi(seat && seat.mapAllowed ? "/ai/autonomy/policy/effective" : null);
  const custodyMode = String((custody.data && custody.data.effective_mode) || "");
  const autoLive = autoLiveEnabled(autoLivePref, custodyMode);
  const toggleRegion = useCallback((id) => {
    setRegionIds((prev) => {
      const next = id == null ? [] : toggleRegionId(prev, id);
      saveRegionIds(next);
      return next;
    });
  }, []);
  const toggleSite = useCallback((id) => {
    setSiteIds((prev) => {
      const next = id == null ? [] : toggleRegionId(prev, id);
      saveSiteIds(next);
      return next;
    });
  }, []);
  const sitesLive = useApi(seat && seat.mapAllowed ? "/resource-fabric/sites" : null);
  const siteRows = ((sitesLive.data && sitesLive.data.items) || []).filter((s) => s && (s.site_id || s.id));
  const setAutoLivePref = useCallback((on) => {
    const pref = on ? "1" : "0";
    saveAutoLivePref(pref);
    setAutoLivePrefState(pref);
  }, []);
  const [volFlashKey, setVolFlashKey] = useState(0);
  const [volFlashOn, setVolFlashOn] = useState(false);
  const s = useSite();
  /* SSE is primary; 5s poll fallback keeps dispatched mobile markers current. */
  const locationsLive = useLocationsLive(5000, 500, seat.teamLocationsAllowed === true);
  const peopleLive = useMemo(
    () => opsPeopleWithLocationsOf(s.people, locationsLive.data).filter((p) => pointInFocus(p && p.ll, viewFocus)),
    [s.people, locationsLive.data, viewFocus],
  );
  const fleet = useSoftFleetSim(s.centre);
  const dispatchEvents = useEventsLive(15000);
  const camRegistry = usePolled("/cameras/registry", 12000);
  const registryItems = useMemo(
    () => ((camRegistry.data && camRegistry.data.items) || []),
    [camRegistry.data],
  );
  const mediaLive = useMemo(() => personnelLiveLampOf(registryItems), [registryItems]);
  const liveDispatchSeen = useRef("");
  useEffect(() => {
    const rows = dispatchEvents.data && Array.isArray(dispatchEvents.data.items)
      ? dispatchEvents.data.items
      : [];
    const ev = rows.find((item) =>
      String(item && item.event_type || "").startsWith("responder.dispatch."),
    );
    if (!ev) return;
    const eid = String(ev.id || ev.event_id || ev.ts_epoch_ms || "");
    if (!eid || eid === liveDispatchSeen.current) return;
    liveDispatchSeen.current = eid;
    const detail = ev.detail && typeof ev.detail === "object" ? ev.detail : {};
    const device = String(detail.device_id || ev.device_id || "").trim();
    if (!device) return;
    const hit = (s.devices || []).find((d) => d && String(d.id) === device)
      || { id: device, label: device };
    setPerson(null);
    setSel(hit);
    setView("site");
    setFly((n) => n + 1);
    toast && toast(
      "Dispatch moved to " + operatorSay(detail.status, "updated")
        + (device ? " · " + boardHeadingOf(hit.label || device) : ""),
      "go",
    );
  }, [dispatchEvents.data, s.devices, toast]);
  const camFocusDone = useRef(false);
  const softTaskFocusDone = useRef(false);
  useEffect(() => {
    if (camFocusDone.current) return;
    let sp = null;
    try { sp = hashSearchParams(); } catch { sp = null; }
    const from = sp && sp.get ? String(sp.get("from") || "") : "";
    if (from !== "camera_focus") return;
    let payload = null;
    try {
      payload = JSON.parse(sessionStorage.getItem("croc.cam.focus") || "null");
    } catch {
      payload = null;
    }
    try { sessionStorage.removeItem("croc.cam.focus"); } catch { /* */ }
    const device = String((payload && payload.device) || (sp.get("device") || "")).trim();
    const zone = String((payload && payload.zone) || (sp.get("zone") || "")).trim();
    if (zone) {
      saveActiveZoneId(zone);
      setActiveZoneId(zone);
    }
    setView("site");
    if (device) {
      const hit = (s.devices || []).find((d) => d && String(d.id) === device);
      if (hit) {
        setSel(hit);
        setFly((n) => n + 1);
        camFocusDone.current = true;
        toast && toast("Map focused from Cameras · " + (hit.label || device), "go");
        return;
      }
      /* wait for devices to load */
      if (!(s.devices || []).length) return;
    }
    camFocusDone.current = true;
    if (zone || device) toast && toast("Map focused from Cameras", "go");
  }, [s.devices, toast]);

  /* Soft Response task stamp → Map Soft focus (Soft≠Live · localhost only). */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const clearSticky = () => {
      try { sessionStorage.removeItem("croc.soft.task.focus"); } catch { /* */ }
    };
    // Soft≠Live: production never paints Soft Plant B / Soft task toast Soft invent Soft sticky.
    if (!isLocalHost()) {
      clearSticky();
      softTaskFocusDone.current = true;
      return;
    }
    const applySoftTask = (payload, { consume = false } = {}) => {
      if (!payload || !payload.soft_demo) return;
      const device = String(payload.device || "").trim();
      const zone = String(payload.zone || "").trim();
      if (zone) {
        try {
          saveActiveZoneId(zone);
          setActiveZoneId(zone);
        } catch { /* */ }
      }
      setView("site");
      if (device) {
        const hit = (s.devices || []).find((d) => d && String(d.id) === device)
          || { id: device, label: payload.taskLabel || device };
        setPerson(null);
        setSel(hit);
        setFly((n) => n + 1);
        toast && toast(
          "Map Soft · task " + (payload.taskLabel || payload.queueId || device)
            + (payload.who ? (" · " + payload.who) : "")
            + " · not Live",
          "warn",
        );
      }
      try {
        window.dispatchEvent(new CustomEvent("croc-soft-focus", {
          detail: { id: device, soft_demo: true, queueId: payload.queueId },
        }));
      } catch { /* */ }
      // Consume-once Soft invent Soft remount re-toast Soft invent Soft Plant B forever.
      if (consume) clearSticky();
    };
    const onSoftTask = (ev) => {
      softTaskFocusDone.current = true;
      applySoftTask(ev && ev.detail, { consume: true });
    };
    window.addEventListener("croc-soft-task-dispatch", onSoftTask);
    if (!softTaskFocusDone.current) {
      let payload = null;
      try {
        payload = JSON.parse(sessionStorage.getItem("croc.soft.task.focus") || "null");
      } catch {
        payload = null;
      }
      if (payload && payload.soft_demo) {
        softTaskFocusDone.current = true;
        applySoftTask(payload, { consume: true });
      }
    }
    return () => window.removeEventListener("croc-soft-task-dispatch", onSoftTask);
  }, [s.devices, toast]);
  useEffect(()=>{ if(person){ setSel(null); setLayersOpen(false); } },[person]);
  useEffect(()=>{ if(view==="globe") setLayersOpen(false); },[view]);
  useEffect(()=>{ if(sel){ setPerson(null); setPanelOpen(true); } },[sel]);

  const enterZone = useCallback((id, route) => {
    const z = String(id || "").trim();
    if (!z) return;
    saveActiveZoneId(z);
    setActiveZoneId(z);
    setSel(null);
    setPerson(null);
    setPanelOpen(true);
    setVolFlashKey((n) => n + 1);
    setVolFlashOn(true);
    try {
      go(typeof route === "string" && route ? route : "/ops/zones");
    } catch {
      /* */
    }
    let openCover = false;
    let label = z;
    try {
      if (isLocalHost() && fleet.state) {
        const cat = softZoneCatalogOf({
          zones: s.zones || [],
          devices: s.devices || [],
          softState: fleet.state,
          centre: fleet.state.origin || s.centre,
        });
        const hit = cat.find((x) => x.id === z || x.label === z);
        if (hit) {
          label = hit.label || z;
          openCover = !(hit.cameraIds && hit.cameraIds.length);
        }
      }
    } catch {
      /* */
    }
    toast &&
      toast(
        openCover
          ? label + " · open cover · Accept on Volumes"
          : "Volume · " + label,
        openCover ? "warn" : "go",
      );
    try {
      window.dispatchEvent(
        new CustomEvent("croc-zone-frame", { detail: { zoneId: z } }),
      );
    } catch {
      /* */
    }
  }, [toast, fleet.state, s.zones, s.devices, s.centre]);

  useEffect(() => {
    if (!volFlashOn) return undefined;
    const t = window.setTimeout(() => setVolFlashOn(false), 920);
    return () => window.clearTimeout(t);
  }, [volFlashOn, volFlashKey]);

  const clearZone = useCallback(() => {
    saveActiveZoneId("");
    setActiveZoneId("");
    toast && toast("Space cleared", "warn");
  }, [toast]);

  useEffect(() => {
    const onClear = () => clearZone();
    window.addEventListener("croc-zone-clear", onClear);
    return () => window.removeEventListener("croc-zone-clear", onClear);
  }, [clearZone]);

  /* Signal → mini live window on the map. Explicit pref, or AUTO custody when unset.
     One window per alarm; respects region filter; never opens while another is up.
     Soft≠Live: the window still needs a real μDock grant to show pixels. */
  const autoLiveSeen = useRef(new Set());
  useEffect(() => {
    if (!autoLive || camLive || !seat.mapAllowed || boardOn || miss) return;
    const region = regionMatcherOf(regionIds, s.zones || []);
    const hit = nextAutoLiveAlarm({
      enabled: true,
      alarms: s.alarms || [],
      seen: autoLiveSeen.current,
      deviceOk: (d) => deviceMatchesSites(d, siteIds, s.zones || []) && region.device(d) && pointInFocus(d && d.ll, viewFocus),
      devices: s.devices || [],
    });
    if (!hit) return;
    autoLiveSeen.current.add(String(hit.alarm.id));
    setCamLive({
      ...hit.device,
      alarmId: hit.alarm.id,
      triggerKind: hit.alarm.by || "signal",
      mini: true,
    });
    toast && toast("Signal · live window · " + (hit.device.label || hit.device.id), "go");
  }, [autoLive, camLive, s.alarms, s.devices, s.zones, regionIds, siteIds, seat.mapAllowed, toast, viewFocus, boardOn, miss]);

  const zoneLabel = useMemo(() => {
    const id = String(activeZoneId || "").trim();
    if (!id) return "";
    if (!isLocalHost() || !fleet.state) return id;
    try {
      const cat = softZoneCatalogOf({
        zones: s.zones || [],
        devices: s.devices || [],
        softState: fleet.state,
        centre: fleet.state.origin,
      });
      const hit = cat.find((z) => z.id === id || z.label === id);
      return (hit && hit.label) || id;
    } catch {
      return id;
    }
  }, [activeZoneId, fleet && fleet.state, s.zones, s.devices]);

  const volumeMapCtx = useMemo(() => {
    const id = String(activeZoneId || "").trim();
    if (!id || !isLocalHost() || !fleet.state) {
      return { ok: false };
    }
    try {
      const cat = softZoneCatalogOf({
        zones: s.zones || [],
        devices: s.devices || [],
        softState: fleet.state,
        centre: fleet.state.origin || s.centre,
      });
      const z = cat.find((x) => x.id === id || x.label === id);
      return softVolumeMapContextOf(z, fleet.state, { devices: s.devices || [] });
    } catch {
      return { ok: false };
    }
  }, [activeZoneId, fleet && fleet.state, s.zones, s.devices, s.centre]);

  const volumeFocus = volumeMapCtx.ok
    ? { origin: volumeMapCtx.origin, radiusM: volumeMapCtx.radiusM }
    : null;

  const gate = s.gate && !res[s.gate.id] ? s.gate : null;
  const resolve = async how => {
    if (!seat || (!seat.decisionAllowed && !seat.hitlAllowed)) {
      toast("Only a manager can continue or hold this plan.", "warn");
      return;
    }
    if (!s.gate || !s.gate.id) return;
    const approved = how === "approve";
    try {
      // P3 Hold SoT Soft≠ invent — Team Runtime only.
      if (!approved) {
        const hold = await holdTeamRunOrRefuse(api, path, s.gate.id, {
          trigger: "ops_map_gate_hold",
        });
        if (!hold.ok) {
          toast(
            holdRefuseToast(hold.reason),
            "warn",
          );
          return;
        }
        setRes(r => ({ ...r, [s.gate.id]: how }));
        toast("Held · Team Runtime", "warn");
        return;
      }
      const cont = await continueTeamRunPrefer(api, path, s.gate.id, {
        trigger: "ops_map_gate_continue",
      });
      if (cont.ok) {
        setRes(r => ({ ...r, [s.gate.id]: how }));
        toast("Continued · Team Runtime", "go");
        return;
      }
      toast(
        continueRefuseToast(cont.reason),
        "warn",
      );
      return;
    } catch (e) {
      const msg = String((e && e.message) || e || "").toLowerCase();
      if (/\b403\b|forbidden|insufficient/.test(msg)) {
        toast("Your account cannot decide on this plan. Ask a manager.", "bad");
      } else if (/\b404\b|not.?found/.test(msg)) {
        toast("That plan is no longer waiting.", "bad");
      } else {
        toast("Could not complete that decision.", "bad");
      }
    }
  };

  const focusDevice = (deviceId) => {
    const id = String(deviceId || "").trim();
    if (!id) return;
    const hit = (s.devices || []).find((d) => d && String(d.id) === id) || { id };
    setPerson(null);
    setSel(hit);
    setFly((f) => f + 1);
    if (view !== "site") setView("site");
  };

    /* Page216 Soft≠Live residual seat vocabulary — open-state no longer painted as map chrome spam. */
  void "Map open";
  void "Decisions closed";
  void "Decisions open";

  const mapLocked = !seat.mapAllowed;
  const regionChip = regionLabelOf(regionIds, s.zones || []);
  const siteChip = siteLabelOf(siteIds, siteRows);
  const showMapRail = !!(mapLocked || activeZoneId || regionChip || siteChip || focusLabel(focus));
  const boardKind = (() => {
    const names = { country: "Country", city: "City", district: "Region", road: "Road" };
    const kinds = [];
    for (const place of focus.places || []) {
      const kind = names[place.level];
      if (kind && kinds.indexOf(kind) < 0) kinds.push(kind);
    }
    return kinds.join(" · ");
  })();
  /* Real div shell (not Fragment): className + data-* bind volume/CSS layout.
     Fragment dropped ops-map-shell entirely — rail/panel padding and cross-
     browser containment broke for every account. */
  return html`<div data-cmc-residual="page216-ops-map" data-cmc-shell="ops-ux" className="ops-map-shell ops-map-shell--liquid ops-map-shell--cc"
    data-ops-ia="command-center"
    data-active-zone=${activeZoneId || ""}
    data-vol-live=${activeZoneId ? "1" : "0"}
    data-vol-flash=${volFlashOn ? "1" : "0"}
    data-vol-ready=${(volumeMapCtx && volumeMapCtx.ready) || ""}
    data-map-open=${seat.mapAllowed ? "1" : "0"}
    data-decisions=${seat.decisionAllowed ? "1" : "0"}
    data-focus-board=${pageOnly ? (miss ? "miss" : "board") : ""}>
    ${showMapRail
      ? html`<div className="ops-map-shell__rail" aria-label=${mapLocked ? "Map access" : "Volume focus"}
          data-rail=${mapLocked ? "seal" : "volume"}>
          ${mapLocked
            ? html`<span className="ops-map-shell__chip" data-on="0" data-lock="1">Map closed</span>
                ${seat.detailLabel
                  ? html`<span className="ops-map-shell__chip ops-map-shell__chip--msg" title=${seat.detailLabel}>
                      ${seat.detailLabel}</span>`
                  : null}
                <button type="button" className="btn btn--ghost btn--sm ops-map-shell__re"
                  disabled=${!seat.reReadAllowed}
                  title="Re-read map seat"
                  onClick=${()=>{
                    if (!seat.reReadAllowed) { toast("Refresh sealed", "warn"); return; }
                    posture.reload && posture.reload();
                    toast("Refreshed", "go");
                  }}>
                  <${Ico} d="refresh" w=${13}/> Refresh</button>`
            : null}
          ${activeZoneId
            ? html`<span className="ops-map-shell__chip" data-zone="1" title="Active area">
                <em className="ops-map-shell__chip-k">Area</em>
                <b className="ops-map-shell__chip-v">${zoneLabel}</b>
                <button type="button" className="ops-map-shell__chip-x" aria-label="Clear area"
                  onClick=${(e)=>{ e.stopPropagation(); clearZone(); }}>×</button>
              </span>`
            : null}
          ${siteChip
            ? html`<span className="ops-map-shell__chip" data-site="1" data-cmc-site-filter="1"
                title="Only these sites are painted">
                <em className="ops-map-shell__chip-k">Sites</em>
                <b className="ops-map-shell__chip-v">${siteChip}</b>
                <button type="button" className="ops-map-shell__chip-x" aria-label="Show every site"
                  onClick=${(e)=>{ e.stopPropagation(); toggleSite(null); toast("Every site", "go"); }}>×</button>
              </span>`
            : null}
          ${focusLabel(focus)
            ? html`<span className="ops-map-shell__chip" data-focus="1" data-cmc-map-focus="1"
                title="Open this board on its own">
                <em className="ops-map-shell__chip-k">Focus</em>
                <button type="button" className="ops-map-shell__chip-v"
                  onClick=${() => { setMiss(""); setFocusOpen(false); commitFocus(enterMapFocus(focus)); }}>
                  ${focusLabel(focus)}
                </button>
                <button type="button" className="ops-map-shell__chip-x" aria-label="Clear map focus"
                  onClick=${(e)=>{ e.stopPropagation(); setMiss(""); commitFocus(emptyFocus()); toast("Whole map", "go"); }}>×</button>
              </span>`
            : null}
          ${regionChip
            ? html`<span className="ops-map-shell__chip" data-region="1" data-cmc-region-filter="1"
                title="Only these regions are painted">
                <em className="ops-map-shell__chip-k">Regions</em>
                <b className="ops-map-shell__chip-v">${regionChip}</b>
                <button type="button" className="ops-map-shell__chip-x" aria-label="Show whole map"
                  onClick=${(e)=>{ e.stopPropagation(); toggleRegion(null); toast("Whole map", "go"); }}>×</button>
              </span>`
            : null}
        </div>`
      : null}
    <${OpsMapPlaneLegend} softOn=${!!(isLocalHost() && fleet.enabled && fleet.state)}/>
    <${OpsVolumeMapRim} ctx=${volumeMapCtx} flashKey=${volFlashKey}/>
    <${AnimatePresence}>
      ${volumeMapCtx && volumeMapCtx.ok
        ? html`<${OpsVolumeMapPlate}
            key=${"plate-" + (volumeMapCtx.id || "vol")}
            ctx=${volumeMapCtx}
            onRun=${() => {
              try { go("/ops/workbench"); } catch { /* */ }
            }}
            onVolumes=${() => {
              try { go("/ops/zones"); } catch { /* */ }
            }}
            onClear=${clearZone}
          />`
        : null}
    <//>
    <${MapCanvas} selected=${sel} onSelect=${d=>{setSel(d);setFly(f=>f+1);}}
      layers=${layers} setNodes=${setNodes} flyKey=${fly} view=${view} setView=${setView}
      onPanel=${()=>{ setLayersOpen(o=>!o); setFocusOpen(false); }} panelOpen=${layersOpen}
      onFocus=${()=>{ setFocusOpen(o=>!o); setLayersOpen(false); }} focusOpen=${focusOpen}
      person=${person} peopleLive=${peopleLive}
      softGeo=${fleet.geo} toast=${toast} seat=${seat}
      activeZoneId=${activeZoneId}
      onZoneEnter=${enterZone}
      sidePanelOpen=${panelOpen}
      registryItems=${registryItems}
      regionIds=${regionIds}
      siteIds=${siteIds}
      focus=${viewFocus}
      fill=${pageOnly}
      softFleetState=${isLocalHost() && fleet.enabled ? fleet.state : null}
      softOps=${isLocalHost() && fleet.enabled && fleet.state ? {
        placeUnit: fleet.placeUnit,
        dispatchTo: fleet.dispatchTo,
        selectUnit: fleet.selectUnit,
        setBearing: fleet.setBearing,
        mode: fleet.state.interactMode,
        selectedId: fleet.state.selectedId,
      } : null}/>
    <${Markers} nodes=${nodes} selected=${sel} onSelect=${setSel} layers=${layers}
      view=${view} setView=${setView} person=${person} onPerson=${openPerson}
      volumeFocus=${volumeFocus} mediaLive=${mediaLive} peopleLive=${peopleLive}/>
    <${AnimatePresence}>
      ${person && html`<${OpsPersonSheetDesk} key=${person} id=${person} onClose=${()=>setPerson(null)}
        trail=${layers.trail} setTrail=${v=>setLayers(x=>({ ...x, trail:v }))} toast=${toast}
        centre=${s.centre} initialTab=${personTab}/>`}
    <//>
    <${AnimatePresence}>
      ${focusOpen && view==="site" && !boardOn && html`
        <${OpsMapFocusDesk} key="focus" focus=${focus}
          onChange=${(next) => {
            const prevIds = new Set((focus.places || []).map((p) => p.id));
            const added = (next.places || []).some((p) => !prevIds.has(p.id));
            const show = next.places.length > 0 && (next.exclusive === true || added);
            commitFocus(show ? enterMapFocus(next) : next);
            if (show) {
              setMiss("");
              setFocusOpen(false);
            }
          }}
          onMiss=${setMiss}
          onClose=${() => { setMiss(""); setFocusOpen(false); }}/>`}
      ${layersOpen && view==="site" && html`
        <${OpsMapLayersDesk} key="lay" layers=${layers} setLayers=${setLayers}
          onClose=${()=>setLayersOpen(false)}
          localSoft=${isLocalHost() && !!(seat && seat.softFleetLocalOnly)} toast=${toast} seat=${seat}
          peopleLive=${peopleLive}
          zones=${s.zones || []} devices=${s.devices || []}
          regionIds=${regionIds} onToggleRegion=${toggleRegion}
          sites=${siteRows} siteIds=${siteIds} onToggleSite=${toggleSite}
          autoLive=${autoLive} autoLivePref=${autoLivePref} onAutoLive=${setAutoLivePref}
          custodyMode=${custodyMode}/>`}
    <//>
    <${AnimatePresence}>
      <${Panel} key=${panelOpen ? "panel-open" : "panel-hidden"}
        tab=${panelTab} selected=${sel} onSelect=${setSel} onPerson=${openPerson} onShot=${setShot}
        onLive=${setCamLive} toast=${toast} fleet=${fleet}
        open=${panelOpen} onToggle=${setPanelOpen}
        activeZoneId=${activeZoneId}
        onEnterZone=${enterZone}
        onClearZone=${clearZone}
        seat=${seat}
        onFocusDevice=${focusDevice}
        onResolved=${(how, out) => {
          const id = out && (out.incident_id || out.id || (out.record && out.record.incident_id));
          if (id) setRes((r) => ({ ...r, [id]: how }));
        }}/>
    <//>
    ${!panelOpen
      ? html`<${DecisionPulse} toast=${toast} onFocusDevice=${focusDevice} seat=${seat}
          onResolved=${(how, out) => {
            const id = out && (out.incident_id || out.id || (out.record && out.record.incident_id));
            if (id) setRes((r) => ({ ...r, [id]: how }));
          }}/>`
      : null}
    <${AnimatePresence} mode="wait">${gate && html`<${Decision} key=${gate.id} gate=${gate} onResolve=${resolve} seat=${seat}/>`}<//>
    <${AnimatePresence}>${shot && html`<${Lightbox} shot=${shot} onClose=${()=>setShot(null)}/>`}<//>
    <${AnimatePresence}>${camLive && html`<${Live} dev=${camLive} onClose=${()=>setCamLive(null)} toast=${toast}/>`}<//>
    ${pageOnly ? html`
      <div className="ops-focus-only" data-cmc-focus-board=${miss ? "miss" : "board"} role="region"
        aria-label=${miss ? "No matching board" : ("Focused board " + focusLabel(focus))}>
        <div className="ops-focus-only__bar">
          <em>${miss ? "Focus" : (boardKind || "Board")}</em>
          <b>${miss ? "No matching board" : focusLabel(focus)}</b>
          <button type="button" className="ops-focus-only__leave" onClick=${leaveBoard}>Leave focus</button>
        </div>
        ${miss ? html`<div className="ops-focus-only__miss" role="status"><p>${miss}</p></div>` : null}
      </div>` : null}
  </div>`;
}

export { PANEL_TABS, OPS_PANEL_NAV };
